import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import { getSectionDetail } from '../actions/courseActions'
import Loader from '../components/Loader'
import Message from '../components/Message'
import axios from 'axios'

// ─── Simple Markdown → HTML renderer (no extra library) ─────────────────────
function slugify(text) {
    return text
        .replace(/&amp;/gi, 'and')   // already-escaped ampersand (from renderMarkdown)
        .replace(/&/g, 'and')        // raw ampersand (from sidebar markdown parsing)
        .replace(/&[a-z]+;/gi, '')   // any other HTML entities
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
}

function renderMarkdown(text) {
    if (!text) return ''
    let html = text
        // Escape HTML entities first
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        // Blockquote → styled callout (must run before > is escaped)
        .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid var(--gold);margin:0.75rem 0;padding:0.5rem 1rem;background:#fffbf0;color:var(--navy);font-style:italic;border-radius:0 6px 6px 0">$1</blockquote>')
        // Strip bare > lines (empty blockquotes / stray markers)
        .replace(/^>\s*$/gm, '')
        .replace(/>/g, '&gt;')
        // #### h4
        .replace(/^#### (.+)$/gm, (_, t) => `<h5 style="color:var(--navy);font-weight:700;font-size:0.92rem;margin-top:1rem;margin-bottom:0.3rem">${t}</h5>`)
        // ### h3
        .replace(/^### (.+)$/gm, (_, t) => `<h4 id="${slugify(t)}" style="color:var(--navy);font-weight:700;margin-top:1.25rem;margin-bottom:0.4rem">${t}</h4>`)
        // ## h2
        .replace(/^## (.+)$/gm, (_, t) => `<h3 id="${slugify(t)}" style="color:var(--gold);font-weight:800;border-bottom:2px solid var(--gold);padding-bottom:6px;margin-top:1.75rem">${t}</h3>`)
        // --- hr
        .replace(/^---$/gm, '<hr style="border:none;border-top:2px dashed #d0d7e3;margin:1.25rem 0">')
        // **bold**
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // `code`
        .replace(/`(.+?)`/g, '<code style="background:#f0f4ff;padding:2px 6px;border-radius:4px;font-size:0.88em">$1</code>')
        // Numbered list items → ol
        .replace(/((?:^\d+\. .+\n?)+)/gm, (block) => {
            const items = block.trim().split('\n').map(l => `<li>${l.replace(/^\d+\. /, '')}</li>`).join('')
            return `<ol style="padding-left:1.6rem;margin-bottom:0.75rem;line-height:1.75">${items}</ol>`
        })
        // Bullet list items → ul
        .replace(/((?:^- .+\n?)+)/gm, (block) => {
            const items = block.trim().split('\n').map(l => `<li>${l.replace(/^- /, '')}</li>`).join('')
            return `<ul style="padding-left:1.4rem;margin-bottom:0.75rem;line-height:1.75">${items}</ul>`
        })
        // Double newline → paragraph break
        .replace(/\n{2,}/g, '</p><p style="margin-bottom:0.6rem">')
        // Single newline between non-block elements → space
        .replace(/([^>])\n([^<])/g, '$1 $2')
    return `<p style="margin-bottom:0.6rem">${html}</p>`
}

// ─── Vocab helpers ──────────────────────────────────────────────────────────────
function buildVocabMap(content) {
    const map = {}
    if (!content) return map
    const re = /\*\*([^*]+)\*\*\s*[–—:-]\s*([^\n]+)/g
    let m
    while ((m = re.exec(content)) !== null) {
        map[m[1].trim()] = m[2].trim()
    }
    return map
}

function renderMarkdownWithTooltips(text, vocabMap) {
    let html = renderMarkdown(text)
    const terms = Object.keys(vocabMap).sort((a, b) => b.length - a.length)
    for (const term of terms) {
        const def = vocabMap[term].replace(/"/g, '&quot;').replace(/'/g, '&#39;')
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp(`(<[^>]*>)|(\\b${escaped}\\b)`, 'gi')
        html = html.replace(re, (match, tag, found) => {
            if (tag) return tag
            return `<span class="vocab-tip" data-def="${def}">${found}</span>`
        })
    }
    return html
}

// ─── Infographic shared styles ────────────────────────────────────────────────
const infoCard  = { background:'var(--white)', borderRadius:'var(--radius)', boxShadow:'var(--shadow)', padding:'1.75rem 2rem', marginTop:'1.5rem' }
const infoTitle = { color:'var(--navy)', fontWeight:800, fontSize:'1.05rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:8, borderBottom:'2px solid var(--gold)', paddingBottom:'0.5rem' }

// ─── Topic 1 Finn explainers ──────────────────────────────────────────────────
function FinnBusinessCycles() {
    return (
        <FinnSays label="FINN EXPLAINS: BUSINESS CYCLES">
            The economy is like a <strong>heartbeat</strong> — it naturally expands and contracts. It's never a straight line up forever, and a dip doesn't mean the world is ending.
            <br /><br />
            <strong>Expansion</strong> — more people have jobs, they spend more money, companies earn more, hire more, repeat. Everything feels good.
            <br />
            <strong>Peak</strong> — the top of the wave. Growth is still happening but prices are rising fast (inflation) and cracks are starting to form.
            <br />
            <strong>Contraction / Recession</strong> — GDP shrinks for 2+ quarters in a row. Companies lay people off, consumers tighten up. Think 2008 or 2020.
            <br />
            <strong>Trough</strong> — the bottom. Things can't get much worse — which means opportunity is actually building. Savvy investors start buying here.
            <br /><br />
            <em>Exam tip: "Cyclical" stocks (cars, homes, restaurants) swing hard with the cycle. "Defensive" stocks (toothpaste, electricity, medication) barely budge.</em>
        </FinnSays>
    )
}

function BusinessCycleWaveSVG() {
    return (
        <div style={{ background:'linear-gradient(135deg,#0d1b2a,#1a2e45)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.06em', marginBottom:'0.4rem' }}>THE ECONOMY'S HEARTBEAT — ONE FULL CYCLE TYPICALLY TAKES 5–10 YEARS</div>
            <svg viewBox="0 0 520 155" style={{ width:'100%', display:'block' }}>
                {/* Recession shading */}
                <rect x="162" y="26" width="138" height="94" fill="rgba(220,53,69,0.08)" rx="4" />
                <text x="231" y="16" textAnchor="middle" fill="#dc3545" fontSize="8" fontWeight="600">RECESSION ZONE</text>
                {/* Shading below wave */}
                <path d="M20,100 C80,100 100,30 160,30 C220,30 240,100 300,100 C360,100 380,30 440,30 C480,30 500,70 510,70 L510,120 L20,120 Z"
                    fill="rgba(240,165,0,0.07)" />
                {/* Wave path */}
                <path d="M20,100 C80,100 100,30 160,30 C220,30 240,100 300,100 C360,100 380,30 440,30 C480,30 500,70 510,70"
                    fill="none" stroke="#f0a500" strokeWidth="3" strokeLinecap="round" />
                {/* Phase dot markers */}
                <circle cx="160" cy="30" r="5" fill="#f0a500" />
                <circle cx="300" cy="100" r="5" fill="#6c757d" />
                <circle cx="440" cy="30" r="5" fill="#f0a500" />
                {/* ── Phase labels — each placed at the CORRECT section of the curve ── */}
                {/* Expansion 1: rising from (20,100) → peak at (160,30) — center ~x=85 */}
                <text x="82" y="118" textAnchor="middle" fill="#28a745" fontSize="10" fontWeight="700">↑ EXPANSION</text>
                {/* Peak 1: actual peak at x=160 */}
                <text x="160" y="21" textAnchor="middle" fill="#f0a500" fontSize="10" fontWeight="700">PEAK</text>
                {/* Contraction 1: falling from (160,30) → trough at (300,100) — center ~x=228 */}
                <text x="228" y="118" textAnchor="middle" fill="#dc3545" fontSize="10" fontWeight="700">↓ CONTRACTION</text>
                {/* Trough: bottom at x=300 */}
                <text x="300" y="133" textAnchor="middle" fill="#6c757d" fontSize="9" fontWeight="700">TROUGH</text>
                {/* Expansion 2: rising from trough (300,100) → peak 2 (440,30) — center ~x=368 */}
                <text x="368" y="118" textAnchor="middle" fill="#28a745" fontSize="10" fontWeight="700">↑ EXPANSION</text>
                {/* Peak 2: actual peak at x=440 */}
                <text x="440" y="21" textAnchor="middle" fill="#f0a500" fontSize="10" fontWeight="700">PEAK</text>
                {/* After peak 2: wave descends again → next contraction beginning */}
                <text x="490" y="118" textAnchor="middle" fill="#dc3545" fontSize="9" fontWeight="600">↓ next...</text>
                {/* Trough-to-trough cycle bracket */}
                <line x1="20" y1="142" x2="300" y2="142" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                <line x1="20" y1="137" x2="20" y2="147" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                <line x1="300" y1="137" x2="300" y2="147" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                <text x="160" y="152" textAnchor="middle" fill="rgba(255,255,255,0.28)" fontSize="7.5">← trough-to-trough = 1 full cycle, historically avg ~6 years →</text>
            </svg>
        </div>
    )
}

function FinnGDPHistory() {
    return (
        <FinnSays label="FINN SAYS: NOW HERE'S THE REAL DATA">
            That wave is idealized — the real economy is messier. Below is actual U.S. GDP growth data going back to 1970. You can literally <em>see</em> the oil-crisis recessions of the 70s, the brutal Volcker double-dip of 1980–82 (intentionally triggered to crush 14% inflation), the mild 2001 dot-com bust, the devastating 2008–09 Great Recession, and COVID 2020 — the sharpest drop in history, but also the shortest recession ever (just 2 months!).
            <br /><br />
            Key takeaway: <strong>cycles play out over years, not weeks.</strong> The expansion after the 2009 trough lasted a record <strong>128 months</strong> — over 10 years — before COVID ended it.
        </FinnSays>
    )
}

function RealGDPHistoryChart() {
    const W = 580, H = 205
    const pL = 46, pR = 14, pT = 44, pB = 28
    const cW = W - pL - pR   // 520
    const cH = H - pT - pB   // 133
    const startYr = 1970, yrR = 54
    const minG = -5, gR = 13  // range: -5% to +8%
    const xOf = yr => pL + (yr - startYr) / yrR * cW
    const yOf = g  => pT + cH - (g - minG) / gR * cH
    const zY = yOf(0)
    const data = [
        [1970,0.2],[1971,3.3],[1972,5.2],[1973,5.6],[1974,-0.5],[1975,-0.2],
        [1976,5.4],[1977,4.6],[1978,5.5],[1979,3.2],[1980,-0.3],[1981,2.5],
        [1982,-1.8],[1983,4.6],[1984,7.2],[1985,4.2],[1986,3.5],[1987,3.5],
        [1988,4.2],[1989,3.7],[1990,1.9],[1991,-0.1],[1992,3.5],[1993,2.8],
        [1994,4.0],[1995,2.7],[1996,3.8],[1997,4.5],[1998,4.5],[1999,4.7],
        [2000,4.1],[2001,1.0],[2002,1.7],[2003,2.9],[2004,3.8],[2005,3.5],
        [2006,2.9],[2007,1.9],[2008,-0.1],[2009,-2.5],[2010,2.6],[2011,1.6],
        [2012,2.2],[2013,1.8],[2014,2.5],[2015,3.1],[2016,1.7],[2017,2.3],
        [2018,3.0],[2019,2.2],[2020,-3.5],[2021,5.9],[2022,2.1],[2023,2.5],
    ]
    const pts = data.map(([yr, g]) => `${xOf(yr).toFixed(0)},${yOf(g).toFixed(0)}`).join(' ')
    // NBER recession bands (start/end in decimal years, label, mid for label)
    const rBands = [
        { s:1973.9, e:1975.25, lbl:'Oil Crisis',  mid:1974.6 },
        { s:1980.1, e:1980.6,  lbl:null,          mid:null },   // too narrow for label
        { s:1981.6, e:1982.9,  lbl:'80 & 82',     mid:1981.8 },
        { s:1990.6, e:1991.25, lbl:'Gulf War',    mid:1990.9 },
        { s:2001.25,e:2001.9,  lbl:'Dot-com',     mid:2001.6 },
        { s:2007.9, e:2009.5,  lbl:'Great Rec.',  mid:2008.7 },
        { s:2019.9, e:2020.5,  lbl:'COVID',       mid:2020.1 },  // widened for visibility; actual: Feb-Apr 2020
    ]
    const decades = [1970,1980,1990,2000,2010,2020]
    const gLevels = [6, 4, 2, 0, -2, -4]
    return (
        <div style={{ background:'linear-gradient(135deg,#0d1b2a,#1a2e45)', borderRadius:12, padding:'1rem', marginBottom:'1rem', overflow:'hidden' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.06em', marginBottom:'0.2rem' }}>REAL U.S. GDP GROWTH — ACTUAL HISTORICAL DATA (1970–2023)</div>
            <div style={{ textAlign:'center', fontSize:'0.68rem', color:'rgba(220,53,69,0.85)', marginBottom:'0.4rem' }}>■ shaded = official recession (NBER)  ·  — — = Fed's 2% target</div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
                {/* Recession bands */}
                {rBands.map((r, i) => (
                    <rect key={i} x={xOf(r.s)} y={pT} width={Math.max(xOf(r.e) - xOf(r.s), 3)} height={cH} fill="rgba(220,53,69,0.22)" />
                ))}
                {/* Recession labels above the band */}
                {rBands.map((r, i) => r.lbl && (
                    <text key={i} x={xOf(r.mid)} y={pT - 5} textAnchor="middle" fill="#ef5350" fontSize="8" fontWeight="700">{r.lbl}</text>
                ))}
                {/* Horizontal grid lines */}
                {gLevels.map(g => (
                    <line key={g} x1={pL} y1={yOf(g)} x2={W - pR} y2={yOf(g)} stroke="rgba(255,255,255,0.07)" />
                ))}
                {/* Y-axis labels */}
                {gLevels.map(g => (
                    <text key={g} x={pL - 4} y={yOf(g) + 4} textAnchor="end" fill="rgba(255,255,255,0.38)" fontSize="8.5"
                    >{g > 0 ? `+${g}%` : `${g}%`}</text>
                ))}
                {/* Zero line */}
                <line x1={pL} y1={zY} x2={W - pR} y2={zY} stroke="rgba(255,255,255,0.38)" strokeWidth="1.5" />
                <text x={pL - 4} y={zY + 4} textAnchor="end" fill="rgba(255,255,255,0.6)" fontSize="8.5" fontWeight="700">0%</text>
                {/* 2% Fed target */}
                <line x1={pL} y1={yOf(2)} x2={W - pR} y2={yOf(2)} stroke="#f0a500" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                <text x={W - pR} y={yOf(2) - 3} textAnchor="end" fill="#f0a500" fontSize="7.5" opacity="0.85">2% target ★</text>
                {/* GDP line */}
                <polyline points={pts} fill="none" stroke="#4ade80" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {/* Axis borders */}
                <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="rgba(255,255,255,0.18)" />
                <line x1={pL} y1={pT + cH} x2={W - pR} y2={pT + cH} stroke="rgba(255,255,255,0.18)" />
                {/* X-axis decade labels */}
                {decades.map(yr => (
                    <g key={yr}>
                        <line x1={xOf(yr)} y1={pT + cH} x2={xOf(yr)} y2={pT + cH + 5} stroke="rgba(255,255,255,0.25)" />
                        <text x={xOf(yr)} y={pT + cH + 15} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">{yr}</text>
                    </g>
                ))}
                {/* Annotated key points */}
                <circle cx={xOf(1984)} cy={yOf(7.2)} r="4" fill="#4ade80" />
                <text x={xOf(1984)} y={yOf(7.2) - 6} textAnchor="middle" fill="#4ade80" fontSize="8" fontWeight="700">+7.2%</text>
                <circle cx={xOf(2009)} cy={yOf(-2.5)} r="4" fill="#ef5350" />
                <text x={xOf(2009)} y={yOf(-2.5) + 14} textAnchor="middle" fill="#ef5350" fontSize="8">−2.5%</text>
                <circle cx={xOf(2020)} cy={yOf(-3.5)} r="4" fill="#ef5350" />
                <text x={xOf(2020) - 4} y={yOf(-3.5) + 4} textAnchor="end" fill="#ef5350" fontSize="8" fontWeight="700">−3.5%</text>
                <circle cx={xOf(2021)} cy={yOf(5.9)} r="4" fill="#4ade80" />
                <text x={xOf(2021)} y={yOf(5.9) - 6} textAnchor="middle" fill="#4ade80" fontSize="7.5">+5.9%</text>
                {/* Source */}
                <text x={pL} y={H - 3} fill="rgba(255,255,255,0.22)" fontSize="6.5">Source: Bureau of Economic Analysis (BEA) · Recession dates: NBER</text>
            </svg>
        </div>
    )
}

function FinnMoneySupply() {
    return (
        <FinnSays label="FINN EXPLAINS: MONEY SUPPLY (M1 & M2)">
            Think of money in <strong>layers of how easy it is to spend.</strong>
            <br /><br />
            <strong>M1 (Most Liquid)</strong> — Cash in your wallet + money in your checking account. This is money you can spend <em>right now</em> without doing anything extra.
            <br /><br />
            <strong>M2 (Broader)</strong> — Everything in M1, <em>plus</em> your savings account, money market funds, and small CDs (certificates of deposit). You can get to it, but it might take a step — transfer it over, wait for maturity, etc.
            <br /><br />
            <em>Why does the Fed watch this?</em> If M2 is growing fast, more money is chasing the same goods → prices rise → inflation. The Fed uses its tools to slow or speed up money creation.
        </FinnSays>
    )
}
function MoneySupplyJarSVG() {
    return (
        <div style={{ background:'linear-gradient(135deg,#eef4ff,#dce8fd)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'rgba(67,97,238,0.65)', letterSpacing:'0.06em', marginBottom:'0.75rem' }}>LAYERS OF MONEY — HOW LIQUID IS IT?</div>
            {/* M2 outer container (dashed border = broader definition) */}
            <div style={{ border:'2px dashed #0dcaf0', borderRadius:14, padding:'0.75rem', background:'rgba(13,202,240,0.04)' }}>
                <div style={{ textAlign:'center', fontWeight:900, fontSize:'1.4rem', color:'#0d6efd', marginBottom:'0.6rem', letterSpacing:'0.02em' }}>BROADER MONEY SUPPLY (M2)</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem' }}>
                    {/* M1 inner solid box */}
                    <div style={{ background:'#4361ee', borderRadius:10, padding:'0.75rem', color:'white' }}>
                        <div style={{ fontWeight:900, fontSize:'1.15rem', marginBottom:2 }}>M1</div>
                        <div style={{ fontSize:'0.7rem', fontWeight:700, opacity:0.75, marginBottom:'0.5rem', letterSpacing:'0.04em' }}>MOST LIQUID</div>
                        <div style={{ fontSize:'0.82rem', lineHeight:1.9 }}>💵 Cash</div>
                        <div style={{ fontSize:'0.82rem', lineHeight:1.9 }}>🏦 Checking accounts</div>
                    </div>
                    {/* M2 extras */}
                    <div style={{ padding:'0.25rem 0.1rem' }}>
                        <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#0d6efd', marginBottom:'0.5rem', textTransform:'uppercase', letterSpacing:'0.04em' }}>M2 also adds:</div>
                        <div style={{ fontSize:'0.82rem', color:'#333', lineHeight:1.9 }}>
                            <div>💰 Savings accounts</div>
                            <div>📈 Money market funds</div>
                            <div>📄 Small CDs</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function FinnTradeAndCurrency() {
    return (
        <FinnSays label="FINN EXPLAINS: TRADE & CURRENCY">
            A <strong>trade deficit</strong> means the U.S. is buying more stuff from other countries than it's selling to them. The U.S. has run a trade deficit for decades — we love imports.
            <br /><br />
            Now think about the dollar like a price tag. If the <strong>dollar is strong</strong>, a $10 American widget costs a foreign buyer 10 euros. If the dollar gets <strong>weaker</strong>, that same widget might only cost them 8 euros — so foreigners buy more of it. That's why a <em>weak dollar is good for U.S. exports</em> but makes your vacation to Europe more expensive.
            <br /><br />
            <em>Strong dollar → great for importers, tourists going abroad, foreign goods get cheaper here.</em><br />
            <em>Weak dollar → great for U.S. exporters, foreign tourism to the U.S., American manufacturers.</em>
        </FinnSays>
    )
}
function TradeSeesaw() {
    // beam: (50,95) → (370,70). angle = atan2(-25,320) ≈ -4.5°
    return (
        <div style={{ background:'linear-gradient(135deg,#fff8e6,#ffedc0)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontWeight:900, fontSize:'1rem', color:'#f0a500', marginBottom:1 }}>TRADE DEFICIT = Imports &gt; Exports</div>
            <div style={{ textAlign:'center', fontSize:'0.7rem', color:'#888', marginBottom:'0.4rem' }}>U.S. current state — more red than green</div>
            <svg viewBox="0 0 420 120" style={{ width:'100%', display:'block' }}>
                {/* Fulcrum */}
                <polygon points="210,103 196,118 224,118" fill="#555" />
                <rect x="208" y="92" width="4" height="13" fill="#555" />
                {/* Board tilted: left end lower (exports lighter), right end lower (imports heavier) */}
                <line x1="50" y1="95" x2="370" y2="70" stroke="#4361ee" strokeWidth="6" strokeLinecap="round" />
                {/* LEFT — EXPORTS box, rotated to sit on beam, center ~(105, 68) */}
                <g transform="rotate(-4.5, 105, 68)">
                    <rect x="50" y="44" width="110" height="48" rx="9" fill="#28a745" fillOpacity="0.18" stroke="#28a745" strokeWidth="1.5" />
                    <text x="105" y="64" textAnchor="middle" fill="#28a745" fontSize="12" fontWeight="800">📦 EXPORTS</text>
                    <text x="105" y="80" textAnchor="middle" fill="#28a745" fontSize="9" fontWeight="600">We sell abroad</text>
                </g>
                {/* RIGHT — IMPORTS box, rotated to sit on beam, center ~(315, 44) */}
                <g transform="rotate(-4.5, 315, 44)">
                    <rect x="260" y="20" width="110" height="48" rx="9" fill="#dc3545" fillOpacity="0.18" stroke="#dc3545" strokeWidth="1.5" />
                    <text x="315" y="40" textAnchor="middle" fill="#dc3545" fontSize="12" fontWeight="800">🛒 IMPORTS</text>
                    <text x="315" y="56" textAnchor="middle" fill="#dc3545" fontSize="9" fontWeight="600">We buy from abroad</text>
                </g>
            </svg>
        </div>
    )
}

function TradeHistoryChart() {
    // Single scale 0–4500B: exports (green) vs imports (red-orange).
    // The gap between the two lines IS the deficit — shaded red.
    const W = 580, H = 210
    const pL = 52, pR = 14, pT = 40, pB = 28
    const cW = W - pL - pR
    const cH = H - pT - pB
    const startYr = 1980, yrR = 44
    const minV = 0, maxV = 4500, vR = maxV - minV
    const xOf = yr  => pL + (yr - startYr) / yrR * cW
    const yOf = val => pT + cH - (val - minV) / vR * cH

    const exData = [
        [1980,280],[1981,305],[1982,280],[1983,270],[1984,290],[1985,290],
        [1986,310],[1987,352],[1988,430],[1989,504],[1990,535],[1991,580],
        [1992,617],[1993,642],[1994,703],[1995,794],[1996,851],[1997,934],
        [1998,934],[1999,966],[2000,1073],[2001,1001],[2002,975],[2003,1020],
        [2004,1160],[2005,1285],[2006,1446],[2007,1645],[2008,1831],[2009,1570],
        [2010,1840],[2011,2103],[2012,2196],[2013,2280],[2014,2376],[2015,2231],
        [2016,2209],[2017,2329],[2018,2500],[2019,2500],[2020,2131],[2021,2559],
        [2022,3000],[2023,3050],
    ]
    // imports = exports - balance (balance is negative, so imports > exports)
    const deficits = [
        25,28,36,67,112,122,145,153,119,93,80,31,
        84,115,151,158,170,181,230,330,445,378,421,495,
        609,708,753,700,698,381,500,560,540,476,505,500,
        480,514,621,577,656,845,1177,1063,
    ]
    const imData = exData.map(([yr, ex], i) => [yr, ex + deficits[i]])

    const exPts = exData.map(([yr, v]) => `${xOf(yr).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ')
    const imPts = imData.map(([yr, v]) => `${xOf(yr).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ')

    // Deficit shading polygon: imports line forward, exports line backward
    const deficitPoly = [
        ...imData.map(([yr, v]) => `${xOf(yr).toFixed(1)},${yOf(v).toFixed(1)}`),
        ...[...exData].reverse().map(([yr, v]) => `${xOf(yr).toFixed(1)},${yOf(v).toFixed(1)}`),
    ].join(' ')

    const decades  = [1980,1990,2000,2010,2020]
    const gridVals = [0,500,1000,1500,2000,2500,3000,3500,4000]

    // Key annotations: [year, exportVal, importVal, deficit label, dy export label]
    const annots = [
        { yr:1991, ex:580, im:611,  lbl:'−$31B\nGulf War low', side:'above', color:'#4ade80' },
        { yr:2006, ex:1446, im:2199, lbl:'−$753B peak', side:'below', color:'#f87171' },
        { yr:2009, ex:1570, im:1951, lbl:'−$381B\nrecession dip', side:'below', color:'#fb923c' },
        { yr:2022, ex:3000, im:4177, lbl:'−$1.18T\nrecord', side:'below', color:'#f87171' },
    ]

    return (
        <div style={{ background:'linear-gradient(135deg,#0d1b2a,#1a2e45)', borderRadius:12, padding:'1rem', marginBottom:'1rem', overflow:'hidden' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.06em', marginBottom:'0.15rem' }}>U.S. EXPORTS vs. IMPORTS — THE GROWING GAP (1980–2023)</div>
            <div style={{ display:'flex', justifyContent:'center', gap:'1.2rem', fontSize:'0.66rem', marginBottom:'0.4rem' }}>
                <span><span style={{ color:'#4ade80', fontWeight:700 }}>——</span> Exports</span>
                <span><span style={{ color:'#fb923c', fontWeight:700 }}>——</span> Imports</span>
                <span><span style={{ color:'rgba(248,113,113,0.6)', fontWeight:700 }}>▓</span> Trade Deficit (gap)</span>
                <span style={{ color:'rgba(255,255,255,0.3)' }}>Billions USD</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
                {/* Grid */}
                {gridVals.map(v => (
                    <g key={v}>
                        <line x1={pL} y1={yOf(v)} x2={W-pR} y2={yOf(v)} stroke="rgba(255,255,255,0.06)" />
                        <text x={pL-4} y={yOf(v)+4} textAnchor="end" fill="rgba(255,255,255,0.3)" fontSize="7.5">
                            {v === 0 ? '$0' : `$${v/1000 >= 1 ? (v/1000)+'T' : v+'B'}`}
                        </text>
                    </g>
                ))}
                {/* Deficit shading between import and export lines */}
                <polygon points={deficitPoly} fill="rgba(248,113,113,0.22)" />
                {/* Export filled area (below export line to baseline) */}
                <polygon
                    points={[
                        ...exData.map(([yr, v]) => `${xOf(yr).toFixed(1)},${yOf(v).toFixed(1)}`),
                        `${xOf(2023).toFixed(1)},${yOf(0).toFixed(1)}`,
                        `${xOf(1980).toFixed(1)},${yOf(0).toFixed(1)}`,
                    ].join(' ')}
                    fill="rgba(74,222,128,0.09)"
                />
                {/* Export line */}
                <polyline points={exPts} fill="none" stroke="#4ade80" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
                {/* Import line */}
                <polyline points={imPts} fill="none" stroke="#fb923c" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
                {/* Axes */}
                <line x1={pL} y1={pT} x2={pL} y2={pT+cH} stroke="rgba(255,255,255,0.18)" />
                <line x1={pL} y1={pT+cH} x2={W-pR} y2={pT+cH} stroke="rgba(255,255,255,0.18)" />
                {/* X decade labels */}
                {decades.map(yr => (
                    <g key={yr}>
                        <line x1={xOf(yr)} y1={pT+cH} x2={xOf(yr)} y2={pT+cH+5} stroke="rgba(255,255,255,0.2)" />
                        <text x={xOf(yr)} y={pT+cH+15} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="9">{yr}</text>
                    </g>
                ))}
                {/* Deficit annotations — midpoint between export and import lines */}
                {annots.map((a, i) => {
                    const midY = (yOf(a.ex) + yOf(a.im)) / 2
                    const lines = a.lbl.split('\n')
                    return (
                        <g key={i}>
                            <circle cx={xOf(a.yr)} cy={midY} r="3" fill={a.color} />
                            {lines.map((l, li) => (
                                <text key={li} x={xOf(a.yr)} y={midY + (a.side === 'above' ? -8 + li*9 : 14 + li*9)}
                                    textAnchor="middle" fill={a.color} fontSize="7" fontWeight="700">{l}</text>
                            ))}
                        </g>
                    )
                })}
                {/* End labels */}
                <text x={xOf(2023)+4} y={yOf(3050)+4} fill="#4ade80" fontSize="8" fontWeight="700">$3.05T</text>
                <text x={xOf(2023)+4} y={yOf(4113)+4} fill="#fb923c" fontSize="8" fontWeight="700">$4.11T</text>
                {/* Source */}
                <text x={pL} y={H-3} fill="rgba(255,255,255,0.2)" fontSize="6">Source: U.S. Census Bureau · Bureau of Economic Analysis (BEA)</text>
            </svg>
        </div>
    )
}

function TradeAndDollarChart() {
    // DUAL-AXIS: Left = DXY (70–160), Right = Trade B USD (-2250 to 4500)
    // Key alignment: DXY=100 and Trade=0 share the SAME horizontal reference line.
    //   DXY: (160-100)/90 = 66.7% from top   →  yDXY(100) = pT + cH*0.667
    //   Trade: 4500/(4500+2250) = 66.7% from top → yTr(0) = pT + cH*0.667  ✓
    const W = 580, H = 245
    const pL = 46, pR = 52, pT = 42, pB = 28
    const cW = W - pL - pR, cH = H - pT - pB
    const startYr = 1980, yrR = 44
    const xOf  = yr  => pL + (yr - startYr) / yrR * cW
    const yDXY = v   => pT + cH - (v - 70)    / 90   * cH   // left axis: 70–160
    const yTr  = v   => pT + cH - (v + 2250)  / 6750 * cH   // right axis: -2250–4500
    const zeroY = yDXY(100)  // === yTr(0)

    const dxyData = [
        [1980,87],[1981,103],[1982,116],[1983,125],[1984,138],[1985,143],
        [1986,112],[1987,96],[1988,92],[1989,98],[1990,89],[1991,92],
        [1992,87],[1993,101],[1994,93],[1995,85],[1996,87],[1997,97],
        [1998,101],[1999,101],[2000,109],[2001,115],[2002,105],[2003,92],
        [2004,86],[2005,88],[2006,83],[2007,76],[2008,80],[2009,78],
        [2010,78],[2011,74],[2012,80],[2013,81],[2014,87],[2015,97],
        [2016,102],[2017,93],[2018,95],[2019,97],[2020,90],[2021,93],
        [2022,109],[2023,103],
    ]
    const exData = [
        [1980,280],[1981,305],[1982,280],[1983,270],[1984,290],[1985,290],
        [1986,310],[1987,352],[1988,430],[1989,504],[1990,535],[1991,580],
        [1992,617],[1993,642],[1994,703],[1995,794],[1996,851],[1997,934],
        [1998,934],[1999,966],[2000,1073],[2001,1001],[2002,975],[2003,1020],
        [2004,1160],[2005,1285],[2006,1446],[2007,1645],[2008,1831],[2009,1570],
        [2010,1840],[2011,2103],[2012,2196],[2013,2280],[2014,2376],[2015,2231],
        [2016,2209],[2017,2329],[2018,2500],[2019,2500],[2020,2131],[2021,2559],
        [2022,3000],[2023,3050],
    ]
    const deficits = [
        25,28,36,67,112,122,145,153,119,93,80,31,
        84,115,151,158,170,181,230,330,445,378,421,495,
        609,708,753,700,698,381,500,560,540,476,505,500,
        480,514,621,577,656,845,1177,1063,
    ]
    const imData  = exData.map(([yr, ex], i) => [yr, ex + deficits[i]])
    const defData = exData.map(([yr],     i) => [yr, -deficits[i]])

    const dxyPts = dxyData.map(([yr,v]) => `${xOf(yr).toFixed(1)},${yDXY(v).toFixed(1)}`).join(' ')
    const exPts  = exData.map( ([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`).join(' ')
    const imPts  = imData.map( ([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`).join(' ')
    const defPts = defData.map(([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`).join(' ')

    // Red shading between import and export lines (the gap = deficit above zero line)
    const gapPoly = [
        ...imData.map(([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`),
        ...[...exData].reverse().map(([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`),
    ].join(' ')

    // Red shading for deficit line below zero
    const defPoly = [
        `${xOf(1980).toFixed(1)},${zeroY.toFixed(1)}`,
        ...defData.map(([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`),
        `${xOf(2023).toFixed(1)},${zeroY.toFixed(1)}`,
    ].join(' ')

    const decades   = [1980,1990,2000,2010,2020]
    const dxyGrid   = [80,90,110,120,130,140,150]
    const trGridTop = [500,1000,1500,2000,2500,3000,3500,4000]
    const trGridBot = [-500,-1000]

    return (
        <div style={{ background:'linear-gradient(135deg,#0d1b2a,#1a2e45)', borderRadius:12, padding:'1rem', marginBottom:'1rem', overflow:'hidden' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.06em', marginBottom:'0.2rem' }}>
                U.S. TRADE FLOWS & DOLLAR STRENGTH (1980–2023)
            </div>
            <div style={{ display:'flex', justifyContent:'center', gap:'1rem', fontSize:'0.65rem', marginBottom:'0.15rem', flexWrap:'wrap', color:'rgba(255,255,255,0.55)' }}>
                <span><span style={{ color:'#f0a500', fontWeight:800 }}>——</span> DXY (left axis)</span>
                <span><span style={{ color:'#4ade80', fontWeight:800 }}>——</span> Exports (right)</span>
                <span><span style={{ color:'#fb923c', fontWeight:800 }}>——</span> Imports (right)</span>
                <span><span style={{ color:'#f87171', fontWeight:800 }}>- -</span> Deficit line (right)</span>
                <span style={{ color:'rgba(255,255,255,0.3)' }}>· DXY 100 = trade $0 baseline</span>
            </div>
            <div style={{ textAlign:'center', fontSize:'0.6rem', color:'rgba(255,255,255,0.28)', marginBottom:'0.3rem', letterSpacing:'0.03em' }}>
                DXY = U.S. Dollar Index — measures the dollar vs. a basket of 6 currencies (EUR, JPY, GBP, CAD, SEK, CHF)
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
                {/* DXY secondary grid (faint) */}
                {dxyGrid.map(v => (
                    <line key={v} x1={pL} y1={yDXY(v)} x2={W-pR} y2={yDXY(v)} stroke="rgba(255,255,255,0.05)" />
                ))}
                {/* Left axis (DXY) labels */}
                {dxyGrid.map(v => (
                    <text key={v} x={pL-4} y={yDXY(v)+4} textAnchor="end" fill="rgba(240,165,0,0.45)" fontSize="7">{v}</text>
                ))}
                {/* Right axis labels — above zero (trade volumes) */}
                {trGridTop.map(v => (
                    <text key={v} x={W-pR+4} y={yTr(v)+4} textAnchor="start" fill="rgba(255,255,255,0.28)" fontSize="7">
                        {`$${v/1000 >= 1 ? (v/1000)+'T' : v+'B'}`}
                    </text>
                ))}
                {/* Right axis labels — below zero (deficit) */}
                {trGridBot.map(v => (
                    <text key={v} x={W-pR+4} y={yTr(v)+4} textAnchor="start" fill="rgba(248,113,113,0.55)" fontSize="7">
                        {`−$${Math.abs(v/1000) >= 1 ? Math.abs(v/1000)+'T' : Math.abs(v)+'B'}`}
                    </text>
                ))}

                {/* === SHARED REFERENCE LINE: DXY=100 / Trade=0 === */}
                <line x1={pL} y1={zeroY} x2={W-pR} y2={zeroY}
                    stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeDasharray="6,4" />
                <text x={pL-4} y={zeroY-3} textAnchor="end" fill="#f0a500" fontSize="8" fontWeight="700">100</text>
                <text x={W-pR+4} y={zeroY-3} textAnchor="start" fill="rgba(255,255,255,0.7)" fontSize="8" fontWeight="700">$0</text>

                {/* Gap shading: import > export (the visible deficit "gap" between the two lines) */}
                <polygon points={gapPoly} fill="rgba(248,113,113,0.14)" />
                {/* Deficit area below the zero line */}
                <polygon points={defPoly} fill="rgba(248,113,113,0.2)" />

                {/* Export fill (green wash under export line, above zero) */}
                <polygon
                    points={[
                        `${xOf(1980).toFixed(1)},${zeroY.toFixed(1)}`,
                        ...exData.map(([yr,v]) => `${xOf(yr).toFixed(1)},${yTr(v).toFixed(1)}`),
                        `${xOf(2023).toFixed(1)},${zeroY.toFixed(1)}`,
                    ].join(' ')}
                    fill="rgba(74,222,128,0.07)"
                />

                {/* Lines — draw in order so DXY is on top */}
                <polyline points={exPts}  fill="none" stroke="#4ade80" strokeWidth="2"   strokeLinejoin="round" strokeLinecap="round" />
                <polyline points={imPts}  fill="none" stroke="#fb923c" strokeWidth="2"   strokeLinejoin="round" strokeLinecap="round" />
                <polyline points={defPts} fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="5,3" />
                <polyline points={dxyPts} fill="none" stroke="#f0a500" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />

                {/* Axes */}
                <line x1={pL}   y1={pT} x2={pL}   y2={pT+cH} stroke="rgba(240,165,0,0.4)" />
                <line x1={W-pR} y1={pT} x2={W-pR} y2={pT+cH} stroke="rgba(255,255,255,0.2)" />
                <line x1={pL}   y1={pT+cH} x2={W-pR} y2={pT+cH} stroke="rgba(255,255,255,0.18)" />

                {/* X-axis decade ticks */}
                {decades.map(yr => (
                    <g key={yr}>
                        <line x1={xOf(yr)} y1={pT+cH} x2={xOf(yr)} y2={pT+cH+5} stroke="rgba(255,255,255,0.2)" />
                        <text x={xOf(yr)} y={pT+cH+15} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="9">{yr}</text>
                    </g>
                ))}

                {/* DXY annotations */}
                <circle cx={xOf(1985)} cy={yDXY(143)} r="3" fill="#f0a500" />
                <text x={xOf(1985)} y={yDXY(143)-8} textAnchor="middle" fill="#f0a500" fontSize="6.5" fontWeight="700">Plaza Accord</text>
                <circle cx={xOf(2001)} cy={yDXY(115)} r="3" fill="#f0a500" />
                <text x={xOf(2001)} y={yDXY(115)-8} textAnchor="middle" fill="#f0a500" fontSize="6.5">Post-9/11 $</text>
                <circle cx={xOf(2011)} cy={yDXY(74)} r="3" fill="#f0a500" />
                <text x={xOf(2011)} y={yDXY(74)+13} textAnchor="middle" fill="#f0a500" fontSize="6.5">DXY low</text>
                <circle cx={xOf(2022)} cy={yDXY(109)} r="3" fill="#f0a500" />
                <text x={xOf(2022)-2} y={yDXY(109)-8} textAnchor="end" fill="#f0a500" fontSize="6.5">Fed hikes ↑$</text>

                {/* Deficit line annotations */}
                <circle cx={xOf(1991)} cy={yTr(-31)} r="3" fill="#4ade80" />
                <text x={xOf(1991)} y={yTr(-31)+13} textAnchor="middle" fill="#4ade80" fontSize="6.5">−$31B</text>
                <circle cx={xOf(2006)} cy={yTr(-753)} r="3" fill="#f87171" />
                <text x={xOf(2006)-2} y={yTr(-753)-7} textAnchor="end" fill="#f87171" fontSize="6.5">−$753B</text>
                <circle cx={xOf(2022)} cy={yTr(-1177)} r="3.5" fill="#f87171" />
                <text x={xOf(2022)} y={yTr(-1177)+13} textAnchor="middle" fill="#f87171" fontSize="7" fontWeight="700">−$1.18T record</text>

                {/* Axis title labels */}
                <text x={14} y={pT + cH*0.5} textAnchor="middle" fill="rgba(240,165,0,0.55)" fontSize="7.5" fontWeight="700"
                    transform={`rotate(-90,14,${pT + cH*0.5})`}>DXY ←</text>
                <text x={W-14} y={pT + cH*0.5} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="7.5" fontWeight="700"
                    transform={`rotate(90,${W-14},${pT + cH*0.5})`}>→ Trade (B USD)</text>

                {/* Source */}
                <text x={pL} y={H-3} fill="rgba(255,255,255,0.18)" fontSize="6">Source: ICE/Federal Reserve (DXY) · U.S. Census Bureau · BEA</text>
            </svg>
        </div>
    )
}

function FinnInflation() {
    return (
        <FinnSays label="FINN EXPLAINS: INFLATION, DEFLATION & STAGFLATION">
            <strong>Inflation</strong> = your $20 buys less than it did last year. A pizza that was $10 is now $12. The dollar is losing purchasing power. A little inflation (~2%) is considered healthy — it means the economy is growing. Too much and it becomes a problem.
            <br /><br />
            <strong>Deflation</strong> = prices actually fall. Sounds great, right? Wrong. If you know your TV will be cheaper next month, you wait. Everyone waits. Nobody buys. Businesses lose revenue, lay people off, demand collapses further. Japan experienced this for a decade.
            <br /><br />
            <strong>Stagflation</strong> = the worst of all worlds. High inflation <em>AND</em> slow growth <em>AND</em> high unemployment — all at once. The Fed can't easily fix it because the normal fixes contradict each other. Think 1970s oil crisis.
            <br /><br />
            <em>Leading indicators predict it. Coincident indicators confirm it. Lagging indicators (like unemployment) show it after the fact.</em>
        </FinnSays>
    )
}
function InflationTimelineSVG() {
    const items = [
        { year:'Year 1', price:'$10.00', emoji:'🍕', label:'Baseline',    color:'#28a745', size:'1.7rem' },
        { year:'Year 2', price:'$10.20', emoji:'🍕', label:'+2% normal',  color:'#28a745', size:'1.9rem' },
        { year:'Year 3', price:'$11.24', emoji:'🍕', label:'+10% — high!',color:'#dc3545', size:'2.4rem' },
        { year:'Year 4', price:'$9.50',  emoji:'🍕', label:'Deflation ↓', color:'#6f42c1', size:'1.4rem' },
    ]
    return (
        <div style={{ background:'linear-gradient(135deg,#fff5f5,#fff0f5)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'#888', letterSpacing:'0.05em', marginBottom:'0.6rem' }}>WHAT $10 PIZZA LOOKS LIKE YEAR OVER YEAR</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'0.5rem' }}>
                {items.map((item, i) => (
                    <div key={i} style={{ textAlign:'center', background:'white', borderRadius:10, padding:'0.75rem 0.25rem', border:`2px solid ${item.color}33`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end' }}>
                        <div style={{ fontSize:item.size, lineHeight:1, marginBottom:6, transition:'font-size 0.3s' }}>{item.emoji}</div>
                        <div style={{ fontWeight:900, fontSize:'0.95rem', color:item.color }}>{item.price}</div>
                        <div style={{ fontSize:'0.72rem', color:'#999', marginTop:2 }}>{item.year}</div>
                        <div style={{ fontSize:'0.68rem', color:item.color, fontWeight:700, marginTop:4, lineHeight:1.3 }}>{item.label}</div>
                    </div>
                ))}
            </div>
            <div style={{ textAlign:'center', fontSize:'0.69rem', color:'#bbb', marginTop:'0.5rem' }}>Pizza size = relative price. Fed targets ~2%/yr. Deflation = shrinking pizza = economic danger.</div>
        </div>
    )
}
function IndicatorCards() {
    const groups = [
        {
            title:'Leading', icon:'🔮', color:'#4361ee', bg:'#eef0ff',
            sub:'Predict the future',
            items:['Stock prices (S&P 500)','Building permits','Consumer confidence','Avg weekly mfg hours','New orders for capital goods'],
        },
        {
            title:'Coincident', icon:'📍', color:'#f0a500', bg:'#fff8e6',
            sub:'Confirm the present',
            items:['GDP growth rate','Industrial production','Retail sales','Personal income'],
        },
        {
            title:'Lagging', icon:'🔍', color:'#6c757d', bg:'#f8f9fa',
            sub:'Confirm after the fact',
            items:['Unemployment rate ★','Avg duration unemployment','Prime rate','Commercial loans outstanding'],
        },
    ]
    return (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'0.75rem', marginBottom:'1rem' }}>
            {groups.map(g => (
                <div key={g.title} style={{ background:g.bg, borderRadius:12, padding:'1rem', borderTop:`4px solid ${g.color}` }}>
                    <div style={{ fontSize:'1.4rem', marginBottom:4 }}>{g.icon}</div>
                    <div style={{ fontWeight:800, color:g.color, fontSize:'0.95rem' }}>{g.title}</div>
                    <div style={{ fontSize:'0.72rem', color:g.color, fontWeight:600, marginBottom:'0.6rem', opacity:0.8 }}>{g.sub}</div>
                    {g.items.map(item => (
                        <div key={item} style={{ fontSize:'0.77rem', color:'#444', padding:'2px 0', borderBottom:'1px solid rgba(0,0,0,0.05)', lineHeight:1.4 }}>• {item}</div>
                    ))}
                </div>
            ))}
        </div>
    )
}

function FinnCPI() {
    return (
        <FinnSays label="FINN EXPLAINS: CPI — THE SHOPPING CART MEASURE">
            The Bureau of Labor Statistics literally sends people to stores, websites, and landlords every month to record prices of about <strong>80,000 items</strong> in a fixed "basket" — things like rent, groceries, gas, haircuts, and medical care. They compare those prices month-to-month and year-to-year.
            <br /><br />
            If that basket costs $1,000 one year and $1,020 the next → <strong>CPI rose 2% → 2% inflation.</strong>
            <br /><br />
            <strong>Core CPI</strong> strips out food and energy prices because they're volatile (oil prices swing wildly). The Fed prefers Core CPI for making policy decisions — it's a calmer signal.
            <br /><br />
            <em>The Fed targets ~2% annual inflation. Below that = deflationary risk. Above that = tightening time.</em>
        </FinnSays>
    )
}
function CPIBasketSVG() {
    const items = [
        { emoji:'🏠', label:'Housing', pct:'33%', color:'#4361ee' },
        { emoji:'🚗', label:'Transport', pct:'18%', color:'#f0a500' },
        { emoji:'🥦', label:'Food', pct:'15%', color:'#28a745' },
        { emoji:'💊', label:'Medical', pct:'9%', color:'#dc3545' },
        { emoji:'🎓', label:'Education', pct:'7%', color:'#6f42c1' },
        { emoji:'⚡', label:'Energy', pct:'8%', color:'#fd7e14' },
        { emoji:'👕', label:'Apparel', pct:'3%', color:'#0dcaf0' },
        { emoji:'🛎', label:'Other', pct:'7%', color:'#6c757d' },
    ]
    return (
        <div style={{ background:'linear-gradient(135deg,#f8f9fa,#e9ecef)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'#888', letterSpacing:'0.05em', marginBottom:'0.75rem' }}>THE CPI BASKET — WHAT'S INSIDE?</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(90px, 1fr))', gap:'0.5rem' }}>
                {items.map(item => (
                    <div key={item.label} style={{ background:'white', borderRadius:10, padding:'0.6rem', textAlign:'center', border:`2px solid ${item.color}22`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize:'1.4rem' }}>{item.emoji}</div>
                        <div style={{ fontWeight:800, fontSize:'0.95rem', color:item.color }}>{item.pct}</div>
                        <div style={{ fontSize:'0.7rem', color:'#555', marginTop:2 }}>{item.label}</div>
                    </div>
                ))}
            </div>
            <div style={{ textAlign:'center', fontSize:'0.72rem', color:'#888', marginTop:'0.5rem' }}>★ Housing is by far the biggest driver of CPI</div>
        </div>
    )
}

// ─── Topic 2 Finn explainers ──────────────────────────────────────────────────
function FinnBalanceSheet() {
    return (
        <FinnSays label="FINN EXPLAINS: THE BALANCE SHEET">
            Imagine you're doing a financial check-up on yourself right now — today. You list everything you own (car, savings account, laptop) and everything you owe (student loan, credit card, mortgage). What's left over after paying all your debts? That's your <strong>net worth</strong> — or in business terms, <strong>equity</strong>.
            <br /><br />
            <strong>Assets = Liabilities + Equity</strong> always has to balance — that's why it's called a balance sheet. If a company borrows $1M to buy equipment, assets go up $1M AND liabilities go up $1M. It still balances.
            <br /><br />
            <em>Current = due/convertible within 1 year. Long-term = beyond 1 year. Always ask: can they pay their short-term bills? That's what liquidity ratios measure.</em>
        </FinnSays>
    )
}

function FinnIncomeStatement() {
    return (
        <FinnSays label="FINN EXPLAINS: THE INCOME STATEMENT">
            The income statement tells you: <strong>did the company make money over this period?</strong> (Quarter or year — not a snapshot like the balance sheet.)
            <br /><br />
            Think of it like a waterfall — revenue pours in at the top, and every expense is a leak. What survives to the bottom is <strong>net income</strong> — the profit.
            <br /><br />
            <strong>Gross profit</strong> = revenue minus what it cost to make/buy the product (COGS). <strong>EBIT</strong> = gross profit minus operating expenses. <strong>Net income</strong> = after taxes and interest too. Each step matters.
            <br /><br />
            <em>Watch out: a company can show positive net income but still run out of cash. That's why you always check the cash flow statement too.</em>
        </FinnSays>
    )
}

function FinnCashFlow() {
    return (
        <FinnSays label="FINN EXPLAINS: CASH FLOW — THE TRUTH TELLER">
            Net income can be manipulated by accounting choices. Cash flow is much harder to fake — cash either moved or it didn't.
            <br /><br />
            <strong>Operating cash flow</strong> = cash generated from the actual business (selling products/services). This is the most important number. A profitable company with negative operating cash flow is a red flag.
            <br /><br />
            <strong>Investing cash flow</strong> = buying or selling long-term assets (machines, buildings, acquisitions). Usually negative for growing companies — they're spending to grow.
            <br /><br />
            <strong>Financing cash flow</strong> = borrowing money, paying it back, issuing stock, paying dividends. This tells you how the company funds itself.
        </FinnSays>
    )
}

function FinnRatios() {
    return (
        <FinnSays label="FINN EXPLAINS: FINANCIAL RATIOS AT A GLANCE">
            Ratios are just ways to compare one number to another to answer a specific question. There are four key questions:
            <br /><br />
            💧 <strong>Can they pay their short-term bills?</strong> → Liquidity ratios (Current, Quick)<br />
            ⚖️ <strong>Are they too deep in debt?</strong> → Leverage ratios (D/E, Interest Coverage)<br />
            💰 <strong>Are they actually making money?</strong> → Profitability ratios (Margins, ROE, ROA)<br />
            📊 <strong>Is the stock priced fairly?</strong> → Valuation ratios (P/E, P/B, EPS)<br /><br />
            <em>Current Ratio above 1 = good. Quick Ratio is stricter — it leaves out inventory because you can't always sell it fast. Interest Coverage below 1.5x is a danger zone.</em>
        </FinnSays>
    )
}

function FinnValuation() {
    return (
        <FinnSays label="FINN EXPLAINS: VALUATION RATIOS">
            A stock's price alone tells you nothing. $50 per share could be cheap or insanely expensive. Valuation ratios give context.
            <br /><br />
            <strong>P/E ratio</strong> = how many dollars are you paying per dollar of earnings? P/E of 20 means investors pay $20 for every $1 the company earns annually. High P/E = growth expectations baked in. Low P/E = value play or struggling company.
            <br /><br />
            <strong>P/B ratio</strong> = stock price vs. what the company's books say it's worth. P/B below 1 could mean undervalued — or that the business is deteriorating.
            <br /><br />
            <em>These are comparison tools — always compare to industry peers, not in isolation.</em>
        </FinnSays>
    )
}

// ─── Topic 3 Finn explainers ──────────────────────────────────────────────────
function FinnTVM() {
    return (
        <FinnSays label="FINN EXPLAINS: TIME VALUE OF MONEY">
            Would you rather have $100 today or $100 one year from now? Today — always. Because $100 today can be invested and grow. Money has a <strong>time value</strong>.
            <br /><br />
            <strong>Future Value (FV)</strong> = what your money grows <em>to</em>. Put $1,000 in at 5%/year for 3 years → FV = $1,157.63
            <br />
            <strong>Present Value (PV)</strong> = what a future amount is worth <em>today</em>. Being promised $1,157.63 in 3 years at 5% → PV = $1,000
            <br /><br />
            Discounting is the reverse of compounding. The higher the interest rate (discount rate), the less a future payment is worth today. This concept drives bond pricing, stock valuation, and almost every investment decision.
            <br /><br />
            <em>FV = PV × (1 + r)ⁿ &nbsp;|&nbsp; PV = FV ÷ (1 + r)ⁿ. Know both cold.</em>
        </FinnSays>
    )
}

function FinnBeta() {
    return (
        <FinnSays label="FINN EXPLAINS: BETA — HOW WILD IS THIS RIDE?">
            Beta measures how much a stock moves <em>relative to the overall market.</em> The S&P 500 itself has a beta of exactly 1.0 — it's the benchmark.
            <br /><br />
            <strong>β = 1.5</strong> → If the market goes up 10%, this stock tends to go up 15%. If the market drops 10%, this stock drops 15%. More exciting, more risky.
            <br />
            <strong>β = 0.5</strong> → Half as volatile as the market. Think utilities — boring but stable.
            <br />
            <strong>β = -1.0</strong> → Moves <em>opposite</em> the market. Gold miners and inverse ETFs sometimes do this.
            <br /><br />
            <em>Beta only measures systematic (market) risk — the kind you can't diversify away. High beta stocks are not necessarily "bad" — they're just higher risk/higher reward.</em>
        </FinnSays>
    )
}

function FinnSharpe() {
    return (
        <FinnSays label="FINN EXPLAINS: SHARPE RATIO — REWARD PER UNIT OF PAIN">
            Two investors both return 12% this year. But one took crazy risks and the other barely sweated. Who did better? The Sharpe Ratio answers that.
            <br /><br />
            <strong>Sharpe = (Portfolio Return − Risk-Free Rate) ÷ Standard Deviation</strong>
            <br /><br />
            The risk-free rate (usually T-bill rate) is subtracted because that's what you'd earn with zero risk. Standard deviation measures how bumpy the ride was. A higher Sharpe means you earned more <em>per unit of risk you took.</em>
            <br /><br />
            <em>Sharpe above 1.0 = good. Above 2.0 = excellent. Below 0 = you'd have been better off in T-bills.</em>
        </FinnSays>
    )
}

function FinnCAPM() {
    return (
        <FinnSays label="FINN EXPLAINS: CAPM — IS THIS STOCK WORTH THE RISK?">
            CAPM (Capital Asset Pricing Model) answers: <strong>"Given how risky this stock is, what return should I expect?"</strong>
            <br /><br />
            <strong>E(r) = Rf + β × (Rm − Rf)</strong>
            <br /><br />
            Let's say: risk-free rate = 4%, market return = 10%, stock beta = 1.5.<br />
            Expected return = 4% + 1.5 × (10% − 4%) = 4% + 9% = <strong>13%</strong>
            <br /><br />
            If the stock actually returned 15%, it beat expectations → <strong>positive alpha</strong>. If it returned only 10%, it underperformed for its risk level → <strong>negative alpha</strong>.
            <br /><br />
            <em>Alpha = Actual Return − CAPM Expected Return. Positive alpha = the manager added value. Negative alpha = would've been better in an index fund.</em>
        </FinnSays>
    )
}

function FinnRisk() {
    return (
        <FinnSays label="FINN EXPLAINS: SYSTEMATIC VS. UNSYSTEMATIC RISK">
            <strong>Systematic risk</strong> is market-wide — a recession, a pandemic, a rate hike. No matter how many stocks you own, you can't escape it. It affects everything.
            <br /><br />
            <strong>Unsystematic risk</strong> is company- or industry-specific — a CEO gets indicted, a product is recalled, a competitor disrupts the space. <em>This</em> can be diversified away. Own 20–30 different companies across different sectors and their individual blowups barely move your overall portfolio.
            <br /><br />
            <em>The whole point of diversification is to eliminate unsystematic risk so only systematic risk remains. Beta measures how much systematic risk you're taking on.</em>
        </FinnSays>
    )
}

function BusinessCycleDiagram() {    const phases = [
        { label:'Expansion',   icon:'fa-arrow-trend-up',   color:'#28a745', bg:'#d4edda', desc:'GDP rising, low unemployment, rising consumer confidence' },
        { label:'Peak',        icon:'fa-mountain',          color:'#f0a500', bg:'#fff3cd', desc:'Highest output — inflation pressure builds, growth slows' },
        { label:'Contraction', icon:'fa-arrow-trend-down',  color:'#dc3545', bg:'#f8d7da', desc:'2+ quarters of negative GDP — unemployment rises' },
        { label:'Trough',      icon:'fa-circle-dot',        color:'#6c757d', bg:'#e9ecef', desc:'Lowest point — recovery begins, buying opportunities emerge' },
    ]
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-rotate" style={{ color:'var(--gold)' }}></i>The Business Cycle</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
                {phases.map((p, i) => (
                    <div key={p.label} style={{ background:p.bg, borderRadius:10, padding:'1rem', borderLeft:`4px solid ${p.color}`, position:'relative' }}>
                        <div style={{ fontWeight:800, color:p.color, fontSize:'0.95rem', marginBottom:4 }}>
                            <i className={`fa-solid ${p.icon} me-2`}></i>{p.label}
                        </div>
                        <div style={{ fontSize:'0.82rem', color:'#444', lineHeight:1.5 }}>{p.desc}</div>
                        <div style={{ position:'absolute', top:8, right:10, background:p.color, color:'white', borderRadius:'50%', width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:800 }}>{i+1}</div>
                    </div>
                ))}
            </div>
            <div style={{ background:'var(--light-bg)', borderRadius:8, padding:'0.75rem 1rem', fontSize:'0.82rem', color:'var(--muted)' }}>
                <strong style={{ color:'var(--navy)' }}>Exam Tip:</strong> Cyclical stocks (autos, housing) underperform in contractions. Defensive stocks (utilities, consumer staples) hold up better across all phases.
            </div>
        </div>
    )
}

function PolicyComparison() {
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-scale-balanced" style={{ color:'var(--gold)' }}></i>Monetary vs. Fiscal Policy</div>
            <div className="policy-grid">
                <div style={{ background:'var(--navy)', borderRadius:10, padding:'1.25rem', color:'white' }}>
                    <div style={{ fontWeight:800, fontSize:'1rem', color:'var(--gold)', marginBottom:'0.75rem' }}><i className="fa-solid fa-landmark me-2"></i>Monetary Policy</div>
                    <div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.6)', marginBottom:'0.75rem' }}>Controlled by: Federal Reserve</div>
                    {['Open Market Operations (most used)','Reserve Requirements','Discount Rate','Federal Funds Rate target'].map(t => (
                        <div key={t} style={{ display:'flex', gap:6, marginBottom:4, fontSize:'0.84rem', alignItems:'flex-start' }}>
                            <i className="fa-solid fa-circle fa-2xs" style={{ color:'var(--gold)', marginTop:5 }}></i>{t}
                        </div>
                    ))}
                </div>
                <div style={{ background:'#f8f9fa', borderRadius:10, padding:'1.25rem', border:'2px solid var(--navy)' }}>
                    <div style={{ fontWeight:800, fontSize:'1rem', color:'var(--navy)', marginBottom:'0.75rem' }}><i className="fa-solid fa-building-columns me-2"></i>Fiscal Policy</div>
                    <div style={{ fontSize:'0.8rem', color:'var(--muted)', marginBottom:'0.75rem' }}>Controlled by: Congress + President</div>
                    {['Government spending increases/cuts','Tax rate changes','Infrastructure programs','Transfer payments (Social Security)'].map(t => (
                        <div key={t} style={{ display:'flex', gap:6, marginBottom:4, fontSize:'0.84rem', alignItems:'flex-start' }}>
                            <i className="fa-solid fa-circle fa-2xs" style={{ color:'var(--navy)', marginTop:5 }}></i>{t}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function GDPComponents() {
    const comps = [
        { letter:'C',  label:'Consumption',  desc:'Household spending — largest component (~70% of U.S. GDP)', color:'#4361ee', bg:'#eef0ff' },
        { letter:'I',  label:'Investment',    desc:'Business spending on capital, equipment, construction',    color:'#f0a500', bg:'#fff8e6' },
        { letter:'G',  label:'Government',    desc:'Federal, state & local purchases of goods/services',       color:'#28a745', bg:'#d4edda' },
        { letter:'NX', label:'Net Exports',   desc:'Exports − Imports (negative for the U.S. = trade deficit)',color:'#dc3545', bg:'#f8d7da' },
    ]
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-chart-pie" style={{ color:'var(--gold)' }}></i>GDP = C + I + G + NX</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'0.75rem' }}>
                {comps.map(c => (
                    <div key={c.letter} style={{ background:c.bg, borderRadius:10, padding:'1rem', borderTop:`4px solid ${c.color}`, textAlign:'center' }}>
                        <div style={{ fontSize:'1.8rem', fontWeight:900, color:c.color, lineHeight:1 }}>{c.letter}</div>
                        <div style={{ fontWeight:700, color:'#333', fontSize:'0.85rem', margin:'4px 0' }}>{c.label}</div>
                        <div style={{ fontSize:'0.75rem', color:'#666', lineHeight:1.4 }}>{c.desc}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function BalanceSheetEquation() {
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-equals" style={{ color:'var(--gold)' }}></i>The Accounting Equation</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'1rem', flexWrap:'wrap', marginBottom:'1.5rem', background:'var(--navy)', borderRadius:12, padding:'1.25rem' }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'1.75rem', fontWeight:900, color:'var(--gold)' }}>ASSETS</div>
                    <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.6)' }}>What you OWN</div>
                </div>
                <div style={{ fontSize:'2rem', fontWeight:900, color:'white' }}>=</div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'1.75rem', fontWeight:900, color:'#f08080' }}>LIABILITIES</div>
                    <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.6)' }}>What you OWE</div>
                </div>
                <div style={{ fontSize:'2rem', fontWeight:900, color:'white' }}>+</div>
                <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'1.75rem', fontWeight:900, color:'#90ee90' }}>EQUITY</div>
                    <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.6)' }}>What owners have left</div>
                </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                <div>
                    <div style={{ fontWeight:700, color:'var(--gold)', marginBottom:6, fontSize:'0.9rem' }}>Current Assets</div>
                    {['Cash & equivalents','Accounts receivable','Inventory','Prepaid expenses'].map(i => (
                        <div key={i} style={{ fontSize:'0.83rem', padding:'3px 0', borderBottom:'1px solid #eee', color:'#555' }}>+ {i}</div>
                    ))}
                    <div style={{ fontWeight:700, color:'var(--gold)', marginTop:12, marginBottom:6, fontSize:'0.9rem' }}>Long-Term Assets</div>
                    {['Property, Plant & Equipment (PP&E)','Intangibles / Goodwill','Long-term investments'].map(i => (
                        <div key={i} style={{ fontSize:'0.83rem', padding:'3px 0', borderBottom:'1px solid #eee', color:'#555' }}>+ {i}</div>
                    ))}
                </div>
                <div>
                    <div style={{ fontWeight:700, color:'#dc3545', marginBottom:6, fontSize:'0.9rem' }}>Current Liabilities</div>
                    {['Accounts payable','Short-term debt','Accrued liabilities'].map(i => (
                        <div key={i} style={{ fontSize:'0.83rem', padding:'3px 0', borderBottom:'1px solid #eee', color:'#555' }}>+ {i}</div>
                    ))}
                    <div style={{ fontWeight:700, color:'#dc3545', marginTop:12, marginBottom:6, fontSize:'0.9rem' }}>Long-Term Liabilities</div>
                    {['Long-term debt / bonds payable','Deferred tax liabilities'].map(i => (
                        <div key={i} style={{ fontSize:'0.83rem', padding:'3px 0', borderBottom:'1px solid #eee', color:'#555' }}>+ {i}</div>
                    ))}
                    <div style={{ fontWeight:700, color:'#28a745', marginTop:12, marginBottom:6, fontSize:'0.9rem' }}>Shareholders' Equity</div>
                    {["Common stock + APIC","Retained earnings","(− Treasury stock)"].map(i => (
                        <div key={i} style={{ fontSize:'0.83rem', padding:'3px 0', borderBottom:'1px solid #eee', color:'#555' }}>{i}</div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function IncomeStatementWaterfall() {
    const rows = [
        { label:'Revenue (Sales)',              sublabel:null,                                              style:'header', value:'$1,000' },
        { label:'− Cost of Goods Sold (COGS)',  sublabel:null,                                              style:'deduct', value:'(400)'  },
        { label:'Gross Profit',                 sublabel:'Gross Margin = Gross Profit ÷ Revenue',           style:'subtotal',value:'$600'  },
        { label:'− Operating Expenses',         sublabel:'SG&A, R&D, depreciation',                        style:'deduct', value:'(200)'  },
        { label:'Operating Income (EBIT)',       sublabel:'Earnings Before Interest & Taxes',               style:'subtotal',value:'$400'  },
        { label:'− Interest Expense',           sublabel:null,                                              style:'deduct', value:'(50)'   },
        { label:'Pre-Tax Income (EBT)',          sublabel:null,                                              style:'mid',    value:'$350'  },
        { label:'− Income Taxes',               sublabel:null,                                              style:'deduct', value:'(88)'   },
        { label:'Net Income',                   sublabel:'Bottom line — earnings attributable to shareholders', style:'final', value:'$263' },
    ]
    const sm = { header:{ bg:'var(--navy)',color:'white',fw:700 }, deduct:{ bg:'#fff5f5',color:'#dc3545',fw:500,fs:'italic' }, subtotal:{ bg:'#e8f4fd',color:'#0d6efd',fw:700,bt:'2px solid #0d6efd' }, mid:{ bg:'#f8f9fa',color:'#555',fw:600 }, final:{ bg:'var(--gold)',color:'var(--navy)',fw:800 } }
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-chart-column" style={{ color:'var(--gold)' }}></i>Income Statement — Top to Bottom</div>
            {rows.map((row, i) => {
                const s = sm[row.style]
                return (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.6rem 1rem', background:s.bg, color:s.color, fontWeight:s.fw, fontStyle:s.fs, borderTop:s.bt, borderRadius: i===0?'8px 8px 0 0': i===rows.length-1?'0 0 8px 8px':0, borderBottom:'1px solid rgba(0,0,0,0.05)', fontSize:'0.88rem' }}>
                        <div>
                            <div>{row.label}</div>
                            {row.sublabel && <div style={{ fontSize:'0.75rem', fontWeight:400, opacity:0.75, fontStyle:'normal' }}>{row.sublabel}</div>}
                        </div>
                        <div style={{ fontFamily:'monospace', fontStyle:'normal' }}>{row.value}</div>
                    </div>
                )
            })}
        </div>
    )
}

function ThreeStatements() {
    const stmts = [
        { title:'Balance Sheet',        icon:'fa-scale-balanced',      color:'#4361ee', bg:'#eef0ff', timing:'Point in Time (snapshot)', key:'Assets = Liabilities + Equity',    purpose:'Shows financial position — what the company owns and owes' },
        { title:'Income Statement',     icon:'fa-chart-line',           color:'#f0a500', bg:'#fff8e6', timing:'Period of Time',           key:'Revenue → Net Income',             purpose:'Shows profitability — how much the company earned or lost' },
        { title:'Cash Flow Statement',  icon:'fa-money-bill-transfer',  color:'#28a745', bg:'#d4edda', timing:'Period of Time',           key:'Operating + Investing + Financing', purpose:'Shows actual cash movement — can expose earnings manipulation' },
    ]
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-file-invoice-dollar" style={{ color:'var(--gold)' }}></i>The Three Financial Statements</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'1rem' }}>
                {stmts.map(s => (
                    <div key={s.title} style={{ background:s.bg, borderRadius:10, padding:'1.25rem', borderTop:`4px solid ${s.color}`, position:'relative' }}>
                        <i className={`fa-solid ${s.icon}`} style={{ position:'absolute', top:'1rem', right:'1rem', fontSize:'1.4rem', color:s.color, opacity:0.35 }}></i>
                        <div style={{ fontWeight:800, color:'#1a1a2e', fontSize:'0.95rem', marginBottom:4, paddingRight:'2rem' }}>{s.title}</div>
                        <div style={{ fontSize:'0.75rem', color:s.color, fontWeight:600, marginBottom:8 }}>{s.timing}</div>
                        <div style={{ background:'rgba(255,255,255,0.7)', borderRadius:6, padding:'4px 8px', fontSize:'0.8rem', fontFamily:'monospace', fontWeight:700, color:s.color, marginBottom:8 }}>{s.key}</div>
                        <div style={{ fontSize:'0.8rem', color:'#555', lineHeight:1.5 }}>{s.purpose}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function BetaScale() {
    const points = [
        { val:-0.5, label:'Inverse',   color:'#6f42c1', desc:'Moves opposite to market (e.g., inverse ETFs)' },
        { val:0,    label:'No Corr.',  color:'#6c757d', desc:'No market sensitivity' },
        { val:0.5,  label:'Defensive', color:'#28a745', desc:'Less volatile than market (utilities, staples)' },
        { val:1.0,  label:'Market',    color:'#f0a500', desc:'Tracks the index exactly' },
        { val:1.5,  label:'Aggressive',color:'#fd7e14', desc:'More volatile than market (growth stocks)' },
        { val:2.0,  label:'High Beta', color:'#dc3545', desc:'Highly volatile — amplifies market moves' },
    ]
    const range = 2.5 - (-0.5)
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-gauge" style={{ color:'var(--gold)' }}></i>Beta — Market Sensitivity Scale</div>
            <div style={{ position:'relative', height:70, margin:'1rem 0 2.5rem' }}>
                <div style={{ position:'absolute', top:24, left:0, right:0, height:6, background:'linear-gradient(to right,#6f42c1,#6c757d,#28a745,#f0a500,#fd7e14,#dc3545)', borderRadius:3 }}></div>
                {points.map(p => {
                    const pct = ((p.val - (-0.5)) / range) * 100
                    return (
                        <div key={p.val} style={{ position:'absolute', left:`${pct}%`, transform:'translateX(-50%)' }}>
                            <div style={{ width:14, height:14, borderRadius:'50%', background:p.color, border:'2px solid white', boxShadow:'0 2px 6px rgba(0,0,0,0.25)', top:18, position:'relative' }}></div>
                            <div style={{ textAlign:'center', marginTop:6, fontSize:'0.72rem', fontWeight:700, color:p.color, whiteSpace:'nowrap' }}>β={p.val}</div>
                        </div>
                    )
                })}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'0.5rem' }}>
                {points.map(p => (
                    <div key={p.val} style={{ background:'#f8f9fa', borderRadius:8, padding:'0.6rem 0.75rem', borderLeft:`3px solid ${p.color}` }}>
                        <div style={{ fontWeight:700, fontSize:'0.82rem', color:p.color }}>β={p.val} — {p.label}</div>
                        <div style={{ fontSize:'0.75rem', color:'#666', marginTop:2 }}>{p.desc}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function RiskTypes() {
    const sys   = ['Recession / economic downturns','Rising interest rates','Inflation / deflation','Geopolitical events','Market-wide crashes']
    const unsys = ['Company earnings miss','Management fraud or scandal','Product recall','Industry-specific regulation','Competitor disruption']
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-shield-halved" style={{ color:'var(--gold)' }}></i>Systematic vs. Unsystematic Risk</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
                <div style={{ background:'#f8d7da', borderRadius:10, padding:'1.25rem' }}>
                    <div style={{ fontWeight:800, color:'#dc3545', marginBottom:'0.5rem', fontSize:'0.95rem' }}><i className="fa-solid fa-globe me-2"></i>Systematic (Market) Risk</div>
                    <div style={{ fontSize:'0.8rem', background:'#dc3545', color:'white', display:'inline-block', borderRadius:12, padding:'2px 10px', marginBottom:10, fontWeight:700 }}>Cannot be diversified away</div>
                    <div style={{ fontSize:'0.8rem', color:'#666', marginBottom:8 }}>Measured by <strong>Beta (β)</strong></div>
                    {sys.map(s => (
                        <div key={s} style={{ display:'flex', gap:6, marginBottom:4, fontSize:'0.82rem', color:'#444', alignItems:'flex-start' }}>
                            <i className="fa-solid fa-xmark" style={{ color:'#dc3545', marginTop:3 }}></i>{s}
                        </div>
                    ))}
                </div>
                <div style={{ background:'#d4edda', borderRadius:10, padding:'1.25rem' }}>
                    <div style={{ fontWeight:800, color:'#28a745', marginBottom:'0.5rem', fontSize:'0.95rem' }}><i className="fa-solid fa-building me-2"></i>Unsystematic (Specific) Risk</div>
                    <div style={{ fontSize:'0.8rem', background:'#28a745', color:'white', display:'inline-block', borderRadius:12, padding:'2px 10px', marginBottom:10, fontWeight:700 }}>Can be diversified away ✓</div>
                    <div style={{ fontSize:'0.8rem', color:'#666', marginBottom:8 }}>Eliminated through <strong>diversification</strong></div>
                    {unsys.map(s => (
                        <div key={s} style={{ display:'flex', gap:6, marginBottom:4, fontSize:'0.82rem', color:'#444', alignItems:'flex-start' }}>
                            <i className="fa-solid fa-check" style={{ color:'#28a745', marginTop:3 }}></i>{s}
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ background:'var(--light-bg)', borderRadius:8, padding:'0.75rem 1rem', fontSize:'0.82rem', color:'var(--muted)' }}>
                <strong style={{ color:'var(--navy)' }}>Key Concept:</strong> Adding more stocks reduces unsystematic risk toward zero. Only systematic risk remains — even a perfectly diversified portfolio can lose value in a market-wide crash.
            </div>
        </div>
    )
}

function SharpeAndCAPM() {
    return (
        <div style={infoCard}>
            <div style={infoTitle}><i className="fa-solid fa-chart-bar" style={{ color:'var(--gold)' }}></i>Key Risk-Adjusted Formulas</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                <div style={{ background:'var(--navy)', borderRadius:12, padding:'1.25rem', color:'white' }}>
                    <div style={{ color:'var(--gold)', fontWeight:800, marginBottom:'0.75rem' }}>Sharpe Ratio</div>
                    <div style={{ background:'rgba(255,255,255,0.1)', borderRadius:8, padding:'0.75rem', fontFamily:'monospace', fontSize:'1rem', textAlign:'center', marginBottom:'0.75rem' }}>
                        (R<sub>p</sub> − R<sub>f</sub>) ÷ σ
                    </div>
                    {[['Rp','Portfolio return'],['Rf','Risk-free rate'],['σ','Standard deviation (total risk)']].map(([sym,def]) => (
                        <div key={sym} style={{ display:'flex', gap:8, fontSize:'0.82rem', marginBottom:4 }}>
                            <span style={{ color:'var(--gold)', fontFamily:'monospace', minWidth:24 }}>{sym}</span>
                            <span style={{ color:'rgba(255,255,255,0.75)' }}>= {def}</span>
                        </div>
                    ))}
                    <div style={{ marginTop:'0.75rem', fontSize:'0.8rem', color:'rgba(255,255,255,0.55)', fontStyle:'italic' }}>Higher = better return per unit of total risk</div>
                </div>
                <div style={{ background:'#f0f4ff', borderRadius:12, padding:'1.25rem', border:'2px solid #4361ee' }}>
                    <div style={{ color:'#4361ee', fontWeight:800, marginBottom:'0.75rem' }}>CAPM — Expected Return</div>
                    <div style={{ background:'#4361ee', borderRadius:8, padding:'0.75rem', fontFamily:'monospace', fontSize:'0.9rem', textAlign:'center', color:'white', marginBottom:'0.75rem' }}>
                        E(r) = R<sub>f</sub> + β × (R<sub>m</sub> − R<sub>f</sub>)
                    </div>
                    {[['Rf','Risk-free rate'],['β','Beta of the asset'],['Rm','Expected market return'],['Rm−Rf','Market risk premium']].map(([sym,def]) => (
                        <div key={sym} style={{ display:'flex', gap:8, fontSize:'0.82rem', marginBottom:4 }}>
                            <span style={{ color:'#4361ee', fontFamily:'monospace', minWidth:52 }}>{sym}</span>
                            <span style={{ color:'#555' }}>= {def}</span>
                        </div>
                    ))}
                    <div style={{ marginTop:'0.75rem', fontSize:'0.8rem', color:'#888', fontStyle:'italic' }}>Used to determine if a stock is fairly priced given its systematic risk</div>
                </div>
            </div>
        </div>
    )
}

// ─── Shared Calculator helpers ───────────────────────────────────────────────────
function CalcInput({ label, value, onChange, suffix='', placeholder='0' }) {
    return (
        <div style={{ marginBottom:'0.75rem' }}>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--gold)', marginBottom:4, letterSpacing:0.5 }}>{label}</label>
            <div style={{ display:'flex', alignItems:'center', background:'rgba(255,255,255,0.08)', borderRadius:8, border:'1px solid rgba(255,255,255,0.15)', overflow:'hidden' }}>
                <input
                    type="number"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    style={{ flex:1, background:'transparent', border:'none', outline:'none', padding:'0.5rem 0.75rem', color:'white', fontSize:'0.95rem', fontFamily:'monospace' }}
                />
                {suffix && <span style={{ padding:'0.5rem 0.75rem', color:'rgba(255,255,255,0.45)', fontSize:'0.85rem', fontWeight:600, borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{suffix}</span>}
            </div>
        </div>
    )
}

function CalcResult({ label, value, interpretation, color='var(--gold)' }) {
    return (
        <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:10, padding:'1rem 1.25rem', marginTop:'0.75rem', borderLeft:`4px solid ${color}` }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.45)', letterSpacing:1, marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:'2rem', fontWeight:900, color, fontFamily:'monospace', lineHeight:1 }}>{value}</div>
            {interpretation && <div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.65)', marginTop:6 }}>{interpretation}</div>}
        </div>
    )
}

function CalcCard({ title, icon, children }) {
    return (
        <div style={{ background:'var(--navy)', borderRadius:'var(--radius)', padding:'1.5rem', marginTop:'1rem', border:'1px solid rgba(240,165,0,0.2)' }}>
            <div style={{ fontWeight:800, color:'var(--gold)', fontSize:'0.92rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:8 }}>
                <i className={`fa-solid fa-${icon}`}></i>{title}
            </div>
            {children}
        </div>
    )
}

function SharpeCalc() {
    const [rp, setRp] = useState('')
    const [rf, setRf] = useState('')
    const [sd, setSd] = useState('')
    const sharpe = sd && Number(sd) !== 0 ? ((Number(rp) - Number(rf)) / Number(sd)).toFixed(3) : null
    const interp = sharpe === null ? null
        : sharpe < 0   ? '⚠️ Negative — portfolio underperformed the risk-free rate'
        : sharpe < 0.5 ? 'Below average risk-adjusted return'
        : sharpe < 1   ? 'Acceptable risk-adjusted return'
        : sharpe < 2   ? '✓ Good risk-adjusted return'
        : '★ Excellent risk-adjusted return'
    const color = sharpe === null ? 'var(--gold)'
        : sharpe < 0   ? '#dc3545'
        : sharpe < 0.5 ? '#fd7e14'
        : sharpe < 1   ? '#ffc107'
        : '#28a745'
    return (
        <CalcCard title="Sharpe Ratio Calculator" icon="calculator">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.75rem' }}>
                <CalcInput label="Portfolio Return" value={rp} onChange={setRp} suffix="%" />
                <CalcInput label="Risk-Free Rate" value={rf} onChange={setRf} suffix="%" />
                <CalcInput label="Std Deviation (σ)" value={sd} onChange={setSd} suffix="%" />
            </div>
            <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.35)', fontFamily:'monospace', marginTop:2 }}>
                Sharpe = (Rp − Rf) ÷ σ
            </div>
            {sharpe !== null && <CalcResult label="SHARPE RATIO" value={sharpe} interpretation={interp} color={color} />}
        </CalcCard>
    )
}

function CAPMCalc() {
    const [rf, setRf]         = useState('')
    const [beta, setBeta]     = useState('')
    const [rm, setRm]         = useState('')
    const [actual, setActual] = useState('')
    const er = rf !== '' && beta !== '' && rm !== ''
        ? (Number(rf) + Number(beta) * (Number(rm) - Number(rf))).toFixed(2)
        : null
    const alpha = er !== null && actual !== ''
        ? (Number(actual) - Number(er)).toFixed(2)
        : null
    return (
        <CalcCard title="CAPM — Expected Return Calculator" icon="chart-line">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.75rem' }}>
                <CalcInput label="Risk-Free Rate (Rf)" value={rf} onChange={setRf} suffix="%" />
                <CalcInput label="Beta (β)" value={beta} onChange={setBeta} placeholder="e.g. 1.2" />
                <CalcInput label="Market Return (Rm)" value={rm} onChange={setRm} suffix="%" />
            </div>
            <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.35)', fontFamily:'monospace', marginTop:2 }}>
                E(r) = Rf + β × (Rm − Rf)
            </div>
            {er !== null && (
                <>
                    <CalcResult label="EXPECTED RETURN (CAPM)" value={`${er}%`}
                        interpretation={`Market risk premium used: ${(Number(rm) - Number(rf)).toFixed(2)}%`} />
                    <CalcInput label="Actual Return (optional — computes Alpha)" value={actual} onChange={setActual} suffix="%" />
                    {alpha !== null && (
                        <CalcResult
                            label="ALPHA (Actual − Expected)"
                            value={`${Number(alpha) > 0 ? '+' : ''}${alpha}%`}
                            interpretation={Number(alpha) > 0 ? '✓ Positive alpha — outperformed' : Number(alpha) < 0 ? '⚠️ Negative alpha — underperformed' : 'Exactly met expectation'}
                            color={Number(alpha) > 0 ? '#28a745' : Number(alpha) < 0 ? '#dc3545' : 'var(--gold)'}
                        />
                    )}
                </>
            )}
        </CalcCard>
    )
}

function TVMCalc() {
    const [mode, setMode] = useState('fv')
    const [pv,   setPv]   = useState('')
    const [fv,   setFv]   = useState('')
    const [rate, setRate] = useState('')
    const [n,    setN]    = useState('')
    const r = Number(rate) / 100
    const result = mode === 'fv'
        ? (pv !== '' && rate !== '' && n !== '' ? (Number(pv) * Math.pow(1 + r, Number(n))).toFixed(2) : null)
        : (fv !== '' && rate !== '' && n !== '' ? (Number(fv) / Math.pow(1 + r, Number(n))).toFixed(2) : null)
    return (
        <CalcCard title="Time Value of Money Calculator" icon="clock-rotate-left">
            <div style={{ display:'flex', gap:8, marginBottom:'1rem' }}>
                {['fv','pv'].map(m => (
                    <button key={m} onClick={() => setMode(m)} style={{
                        background: mode===m ? 'var(--gold)' : 'rgba(255,255,255,0.08)',
                        color: mode===m ? 'var(--navy)' : 'rgba(255,255,255,0.65)',
                        border:'none', borderRadius:20, padding:'4px 18px',
                        fontWeight:700, fontSize:'0.82rem', cursor:'pointer',
                    }}>Solve for {m.toUpperCase()}</button>
                ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.75rem' }}>
                {mode === 'fv'
                    ? <CalcInput label="Present Value (PV)" value={pv} onChange={setPv} suffix="$" />
                    : <CalcInput label="Future Value (FV)" value={fv} onChange={setFv} suffix="$" />
                }
                <CalcInput label="Rate per Period" value={rate} onChange={setRate} suffix="%" />
                <CalcInput label="Number of Periods" value={n} onChange={setN} suffix="n" />
            </div>
            <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.35)', fontFamily:'monospace', marginTop:2 }}>
                {mode === 'fv' ? 'FV = PV × (1 + r)ⁿ' : 'PV = FV ÷ (1 + r)ⁿ'}
            </div>
            {result !== null && (
                <CalcResult
                    label={mode === 'fv' ? 'FUTURE VALUE' : 'PRESENT VALUE'}
                    value={`$${Number(result).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                    interpretation={mode === 'fv'
                        ? `$${Number(pv).toLocaleString()} grows to $${Number(result).toLocaleString()} over ${n} periods at ${rate}%`
                        : `$${Number(fv).toLocaleString()} in ${n} periods is worth $${Number(result).toLocaleString()} today at ${rate}%`}
                />
            )}
        </CalcCard>
    )
}

function RatioCalc() {
    const [ca,  setCa]  = useState('')
    const [inv, setInv] = useState('')
    const [cl,  setCl]  = useState('')
    const cr = ca !== '' && cl !== '' && Number(cl) !== 0 ? (Number(ca) / Number(cl)).toFixed(2) : null
    const qr = ca !== '' && inv !== '' && cl !== '' && Number(cl) !== 0 ? ((Number(ca) - Number(inv)) / Number(cl)).toFixed(2) : null
    const interpCR = cr === null ? null : Number(cr) < 1 ? '⚠️ Below 1.0 — may struggle to meet short-term obligations' : Number(cr) < 1.5 ? 'Acceptable liquidity' : '✓ Healthy liquidity'
    const interpQR = qr === null ? null : Number(qr) < 1 ? '⚠️ Below 1.0 — depends on selling inventory to pay bills' : '✓ Can meet obligations without selling inventory'
    return (
        <CalcCard title="Liquidity Ratio Calculator" icon="droplet">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.75rem' }}>
                <CalcInput label="Current Assets ($)" value={ca} onChange={setCa} suffix="$" />
                <CalcInput label="Inventory ($)" value={inv} onChange={setInv} suffix="$" />
                <CalcInput label="Current Liabilities ($)" value={cl} onChange={setCl} suffix="$" />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                {cr !== null && <CalcResult label="CURRENT RATIO" value={cr} interpretation={interpCR} color={Number(cr) >= 1.5 ? '#28a745' : Number(cr) >= 1 ? '#ffc107' : '#dc3545'} />}
                {qr !== null && <CalcResult label="QUICK RATIO" value={qr} interpretation={interpQR} color={Number(qr) >= 1 ? '#28a745' : '#dc3545'} />}
            </div>
        </CalcCard>
    )
}

function GDPCalc() {
    const [c,  setC]  = useState('')
    const [i,  setI]  = useState('')
    const [g,  setG]  = useState('')
    const [ex, setEx] = useState('')
    const [im, setIm] = useState('')
    const vals = { C:Number(c)||0, I:Number(i)||0, G:Number(g)||0, Ex:Number(ex)||0, Im:Number(im)||0 }
    const nx   = vals.Ex - vals.Im
    const gdp  = vals.C + vals.I + vals.G + nx
    const hasData = c||i||g||ex||im
    const clrs = { C:'#4361ee', I:'#f0a500', G:'#28a745', NX: nx >= 0 ? '#17a2b8' : '#dc3545' }
    const parts = [{k:'C',v:vals.C},{k:'I',v:vals.I},{k:'G',v:vals.G},{k:'NX',v:nx}]
    return (
        <CalcCard title="GDP Calculator — C + I + G + NX" icon="chart-pie">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'0.5rem' }}>
                <CalcInput label="Consumption (C)" value={c} onChange={setC} />
                <CalcInput label="Investment (I)" value={i} onChange={setI} />
                <CalcInput label="Government (G)" value={g} onChange={setG} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'0.5rem', marginTop:'0.5rem' }}>
                <CalcInput label="Exports" value={ex} onChange={setEx} />
                <CalcInput label="Imports" value={im} onChange={setIm} />
            </div>
            {hasData && (
                <>
                    <CalcResult label="TOTAL GDP" value={`$${gdp.toLocaleString()}`}
                        interpretation={`Net Exports (NX): ${nx >= 0 ? '+' : ''}$${nx.toLocaleString()}`}
                        color={gdp > 0 ? 'var(--gold)' : '#dc3545'} />
                    {gdp > 0 && (
                        <div style={{ marginTop:'0.75rem' }}>
                            {parts.map(p => (
                                <div key={p.k} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                                    <div style={{ minWidth:28, fontSize:'0.8rem', fontWeight:800, color:clrs[p.k] }}>{p.k}</div>
                                    <div style={{ flex:1, height:12, background:'rgba(255,255,255,0.08)', borderRadius:6, overflow:'hidden' }}>
                                        <div style={{ height:'100%', width:`${Math.max(0,(p.v/gdp)*100)}%`, background:clrs[p.k], borderRadius:6, transition:'width 0.4s ease' }}></div>
                                    </div>
                                    <div style={{ minWidth:60, fontSize:'0.78rem', color:'rgba(255,255,255,0.65)', fontFamily:'monospace', textAlign:'right' }}>
                                        {gdp > 0 ? `${((p.v/gdp)*100).toFixed(1)}%` : '—'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </CalcCard>
    )
}

function ValuationCalc() {
    const [price, setPrice] = useState('')
    const [eps,   setEps]   = useState('')
    const [bvps,  setBvps]  = useState('')
    const pe = price && eps  && Number(eps)  !== 0 ? (Number(price) / Number(eps)).toFixed(2)  : null
    const pb = price && bvps && Number(bvps) !== 0 ? (Number(price) / Number(bvps)).toFixed(2) : null
    const interPE = pe === null ? null : Number(pe) < 10 ? 'Low P/E — possible undervaluation or low-growth sector' : Number(pe) < 20 ? 'Moderate P/E — fairly valued' : Number(pe) < 30 ? 'High P/E — significant growth expectations priced in' : '⚠️ Very high P/E — speculative premium'
    const interPB = pb === null ? null : Number(pb) < 1 ? 'P/B below 1 — trading below book value (potential opportunity or distress)' : Number(pb) < 3 ? 'Moderate P/B — typical for mature companies' : 'High P/B — intangibles or growth drive market premium'
    return (
        <CalcCard title="Valuation Ratio Calculator" icon="tag">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.75rem' }}>
                <CalcInput label="Stock Price ($)" value={price} onChange={setPrice} suffix="$" />
                <CalcInput label="EPS ($)" value={eps} onChange={setEps} suffix="$" />
                <CalcInput label="Book Value Per Share ($)" value={bvps} onChange={setBvps} suffix="$" />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                {pe !== null && <CalcResult label="P/E RATIO" value={`${pe}x`} interpretation={interPE} color={Number(pe) < 20 ? '#28a745' : Number(pe) < 30 ? '#ffc107' : '#fd7e14'} />}
                {pb !== null && <CalcResult label="P/B RATIO" value={`${pb}x`} interpretation={interPB} color='#17a2b8' />}
            </div>
        </CalcCard>
    )
}

// ─── Content section parser ───────────────────────────────────────────────────
function parseIntoSections(text) {
    if (!text) return []
    const lines = text.split('\n')
    const sections = []
    let current = null
    for (const line of lines) {
        const h2 = line.match(/^## (.+)$/)
        const h3 = line.match(/^### (.+)$/)
        if (h2) {
            if (current) sections.push(current)
            current = { level: 2, heading: h2[1], body: '' }
        } else if (h3) {
            if (current) sections.push(current)
            current = { level: 3, heading: h3[1], body: '' }
        } else if (current) {
            // skip bare markdown artifacts (lone >, ---, etc. handled by renderer)
            current.body += line + '\n'
        }
    }
    if (current) sections.push(current)
    // strip bodies that are nothing but whitespace and bare > markers
    return sections.map(s => ({ ...s, body: s.body.split('\n').filter(l => l.trim() !== '>').join('\n') }))
}

// ─── Finn: recurring cartoon character used across all explainer cards ────────
function FinnAvatar({ size = 72 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 100 115" style={{ flexShrink:0 }}>
            {/* Shirt */}
            <rect x="22" y="75" width="56" height="40" rx="10" fill="#4361ee" />
            {/* White collar */}
            <polygon points="44,75 50,84 50,75" fill="white" />
            <polygon points="56,75 50,84 50,75" fill="white" />
            {/* Gold tie */}
            <polygon points="50,84 46,96 50,107 54,96" fill="#f0a500" />
            {/* Neck */}
            <rect x="44" y="65" width="12" height="12" fill="#f4c2a1" />
            {/* Head */}
            <circle cx="50" cy="44" r="28" fill="#f4c2a1" />
            {/* Hair */}
            <path d="M22,32 Q22,8 50,12 Q78,8 78,32 Q70,17 50,17 Q30,17 22,32 Z" fill="#4a2c0a" />
            {/* Ears */}
            <ellipse cx="22" cy="45" rx="5" ry="7" fill="#f4c2a1" />
            <ellipse cx="78" cy="45" rx="5" ry="7" fill="#f4c2a1" />
            {/* Eye whites */}
            <ellipse cx="39" cy="44" rx="7.5" ry="7.5" fill="white" />
            <ellipse cx="61" cy="44" rx="7.5" ry="7.5" fill="white" />
            {/* Pupils */}
            <circle cx="40" cy="45" r="4" fill="#1a1a2e" />
            <circle cx="62" cy="45" r="4" fill="#1a1a2e" />
            {/* Eye shine */}
            <circle cx="42" cy="42" r="1.5" fill="white" />
            <circle cx="64" cy="42" r="1.5" fill="white" />
            {/* Glasses */}
            <rect x="30" y="38" width="16" height="11" rx="3" fill="none" stroke="#333" strokeWidth="2" />
            <rect x="54" y="38" width="16" height="11" rx="3" fill="none" stroke="#333" strokeWidth="2" />
            <line x1="46" y1="43" x2="54" y2="43" stroke="#333" strokeWidth="2" />
            <line x1="30" y1="43" x2="23" y2="41" stroke="#333" strokeWidth="1.5" />
            <line x1="70" y1="43" x2="77" y2="41" stroke="#333" strokeWidth="1.5" />
            {/* Smile */}
            <path d="M40,57 Q50,65 60,57" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" />
            {/* Cheeks */}
            <circle cx="29" cy="54" r="4" fill="#f08080" opacity="0.35" />
            <circle cx="71" cy="54" r="4" fill="#f08080" opacity="0.35" />
        </svg>
    )
}
function FinnSays({ children, label = 'FINN EXPLAINS' }) {
    return (
        <div style={{ display:'flex', alignItems:'flex-start', gap:'1rem', background:'linear-gradient(135deg,#eef4ff,#e8f0fe)', borderRadius:14, padding:'1rem 1.25rem', marginBottom:'1.25rem', border:'2px solid #c5d8f8' }}>
            <FinnAvatar size={72} />
            <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, fontSize:'0.68rem', color:'var(--gold)', letterSpacing:'0.08em', marginBottom:'0.35rem', textTransform:'uppercase' }}>{label}</div>
                <div style={{ background:'white', borderRadius:'0 12px 12px 12px', padding:'0.85rem 1rem', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', fontSize:'0.87rem', lineHeight:1.7, color:'var(--navy)' }}>
                    {children}
                </div>
            </div>
        </div>
    )
}

// ─── GDP Town Illustration ────────────────────────────────────────────────────
function GDPTownIllustration() {
    return (
        <div style={{ background:'linear-gradient(180deg,#daeeff 0%,#c5e8b8 80%,#8BC34A 100%)', borderRadius:12, padding:'0.75rem', marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontWeight:800, fontSize:'0.75rem', color:'#1a2e45', marginBottom:'0.4rem', letterSpacing:'0.04em' }}>
                GDP = EVERY TRANSACTION ACROSS THE WHOLE COUNTRY
            </div>
            <svg viewBox="0 0 520 155" style={{ width:'100%', display:'block' }}>
                {/* Ground */}
                <rect x="0" y="128" width="520" height="27" fill="#8BC34A" />

                {/* ── C: House ── */}
                <polygon points="52,42 8,82 96,82" fill="#283593" />
                <rect x="14" y="80" width="76" height="50" rx="2" fill="#4361ee" />
                <rect x="38" y="103" width="20" height="27" fill="#f0a500" rx="2" />
                <rect x="18" y="88" width="16" height="12" fill="#90CAF9" rx="2" />
                <rect x="68" y="88" width="16" height="12" fill="#90CAF9" rx="2" />
                <text x="52" y="35" textAnchor="middle" fill="#283593" fontSize="14" fontWeight="900">C</text>
                <text x="52" y="142" textAnchor="middle" fill="white" fontSize="8.5" fontWeight="700">You buy things</text>

                <text x="106" y="105" textAnchor="middle" fill="#1a2e45" fontSize="22" fontWeight="900">+</text>

                {/* ── I: Factory ── */}
                <rect x="117" y="83" width="76" height="47" rx="2" fill="#FF6F00" />
                <rect x="113" y="79" width="84" height="6" fill="#E65100" rx="1" />
                <rect x="124" y="52" width="10" height="30" fill="#888" rx="2" />
                <rect x="144" y="58" width="10" height="24" fill="#888" rx="2" />
                <rect x="164" y="52" width="10" height="30" fill="#888" rx="2" />
                <circle cx="129" cy="47" r="6" fill="#ccc" opacity="0.7" />
                <circle cx="149" cy="53" r="5" fill="#ccc" opacity="0.6" />
                <circle cx="169" cy="47" r="6" fill="#ccc" opacity="0.7" />
                <rect x="122" y="92" width="14" height="10" fill="#FFF9C4" rx="1" />
                <rect x="145" y="92" width="14" height="10" fill="#FFF9C4" rx="1" />
                <rect x="168" y="92" width="14" height="10" fill="#FFF9C4" rx="1" />
                <text x="155" y="45" textAnchor="middle" fill="#E65100" fontSize="14" fontWeight="900">I</text>
                <text x="155" y="142" textAnchor="middle" fill="white" fontSize="8.5" fontWeight="700">Companies build</text>

                <text x="208" y="105" textAnchor="middle" fill="#1a2e45" fontSize="22" fontWeight="900">+</text>

                {/* ── G: Capitol ── */}
                <rect x="221" y="120" width="76" height="8" fill="#1B5E20" rx="1" />
                <rect x="226" y="108" width="66" height="13" fill="#2E7D32" rx="1" />
                <rect x="231" y="80" width="56" height="30" fill="#2E7D32" />
                {[234,246,258,270,282].map(x => (
                    <rect key={x} x={x} y="80" width="6" height="30" fill="#A5D6A7" opacity="0.6" />
                ))}
                <ellipse cx="259" cy="76" rx="30" ry="14" fill="#388E3C" />
                <ellipse cx="259" cy="76" rx="18" ry="8" fill="#2E7D32" />
                <rect x="257" y="50" width="4" height="28" fill="#1B5E20" />
                <polygon points="259,41 254,52 264,52" fill="#f0a500" />
                <text x="259" y="35" textAnchor="middle" fill="#1B5E20" fontSize="14" fontWeight="900">G</text>
                <text x="259" y="142" textAnchor="middle" fill="white" fontSize="8.5" fontWeight="700">Gov spends</text>

                <text x="312" y="105" textAnchor="middle" fill="#1a2e45" fontSize="22" fontWeight="900">+</text>

                {/* ── NX: Cargo Ship ── */}
                <rect x="320" y="120" width="148" height="12" fill="#1565C0" opacity="0.25" rx="3" />
                <path d="M326,119 L338,132 L452,132 L464,119 Z" fill="#c0392b" />
                <rect x="333" y="103" width="128" height="18" fill="#922b21" />
                <rect x="422" y="83" width="33" height="22" fill="#e74c3c" rx="2" />
                <rect x="426" y="87" width="8" height="6" fill="#90CAF9" rx="1" />
                <rect x="438" y="87" width="8" height="6" fill="#90CAF9" rx="1" />
                <rect x="432" y="67" width="10" height="18" fill="#555" rx="2" />
                <circle cx="437" cy="63" r="5" fill="#bbb" opacity="0.7" />
                <rect x="337" y="88" width="28" height="17" fill="#F9A825" rx="2" />
                <rect x="369" y="88" width="28" height="17" fill="#1565C0" rx="2" />
                <rect x="401" y="88" width="18" height="17" fill="#2E7D32" rx="2" />
                <rect x="340" y="77" width="22" height="13" fill="#F57F17" rx="2" />
                <rect x="366" y="77" width="22" height="13" fill="#0D47A1" rx="2" />
                <path d="M320,125 Q335,119 350,125 Q365,119 380,125 Q395,119 410,125 Q425,119 440,125 Q455,119 465,125" fill="none" stroke="#1565C0" strokeWidth="1.5" opacity="0.5" />
                <text x="393" y="70" textAnchor="middle" fill="#c0392b" fontSize="13" fontWeight="900">NX</text>
                <text x="393" y="148" textAnchor="middle" fill="white" fontSize="8.5" fontWeight="700">Exports − Imports</text>

                <text x="487" y="99" textAnchor="middle" fill="#1a2e45" fontSize="17" fontWeight="900">=</text>
                <text x="487" y="116" textAnchor="middle" fill="#1a2e45" fontSize="12" fontWeight="900">GDP</text>
            </svg>
        </div>
    )
}
function GDPPlainEnglish() {
    return (
        <>
            <FinnSays>
                Think of GDP like a <strong>giant receipt for the whole country.</strong> Every time you buy groceries, a company builds a warehouse, the government fixes a road, or we sell a car to someone in Japan — it all gets counted. GDP just asks: <em>"How much total stuff did we produce and sell inside this country this year?"</em>
                <br /><br />
                <strong>Real GDP</strong> strips out the effect of rising prices (inflation) so you can actually compare year-to-year. <strong>Nominal GDP</strong> uses today's prices — the headline number, but misleading if prices jumped a lot. <strong>Capital equipment</strong> (the "I" in the formula) just means physical stuff companies use to produce things — think factory machines, trucks, computers, buildings.
            </FinnSays>
            <GDPTownIllustration />
        </>
    )
}

// ─── Bond & Yield Curve explainers ───────────────────────────────────────────
function BondLifecycleCard() {
    const steps = [
        { icon:'💵', title:'You Lend $1,000', sub:'You buy the bond — you\'re the lender', color:'#4361ee', bg:'#eef0ff' },
        { icon:'📅', title:'Annual Interest', sub:'$40/yr (4% coupon) paid to you each year', color:'#f0a500', bg:'#fff8e6' },
        { icon:'🏁', title:'Maturity Date', sub:'You get your $1,000 back in full', color:'#28a745', bg:'#d4edda' },
    ]
    return (
        <div style={{ marginBottom:'1rem' }}>
            <div style={{ textAlign:'center', fontWeight:800, fontSize:'0.75rem', color:'var(--navy)', marginBottom:'0.75rem', letterSpacing:'0.04em' }}>HOW A BOND WORKS — STEP BY STEP</div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', justifyContent:'center' }}>
                {steps.map((s, i) => (
                    <React.Fragment key={i}>
                        <div style={{ background:s.bg, borderRadius:12, padding:'1rem', textAlign:'center', flex:'1', minWidth:120, border:`2px solid ${s.color}22` }}>
                            <div style={{ fontSize:'1.8rem', marginBottom:4 }}>{s.icon}</div>
                            <div style={{ fontWeight:800, fontSize:'0.85rem', color:s.color, marginBottom:2 }}>{s.title}</div>
                            <div style={{ fontSize:'0.76rem', color:'#555', lineHeight:1.4 }}>{s.sub}</div>
                        </div>
                        {i < steps.length - 1 && <div style={{ fontSize:'1.5rem', color:'#ccc', flexShrink:0 }}>→</div>}
                    </React.Fragment>
                ))}
            </div>
            <div style={{ display:'flex', gap:'1.5rem', justifyContent:'center', marginTop:'0.75rem', flexWrap:'wrap' }}>
                <div style={{ fontSize:'0.8rem', color:'#555' }}><strong style={{ color:'var(--navy)' }}>Maturity</strong> — the end date when principal is returned</div>
                <div style={{ fontSize:'0.8rem', color:'#555' }}><strong style={{ color:'var(--navy)' }}>Yield</strong> — your actual annual return as a % (moves opposite to price)</div>
                <div style={{ fontSize:'0.8rem', color:'#555' }}><strong style={{ color:'var(--navy)' }}>Coupon</strong> — the fixed interest payment promised</div>
            </div>
        </div>
    )
}
function YieldCurveChartSVG() {
    const mats = [
        { label:'1yr',  x:80  },
        { label:'2yr',  x:158 },
        { label:'5yr',  x:248 },
        { label:'10yr', x:345 },
        { label:'30yr', x:430 },
    ]
    const toY = r => 150 - (r - 2) * 32
    const normalRates   = [3.0, 3.5, 4.0, 4.5, 5.0]
    const flatRates     = [4.0, 4.0, 4.0, 4.0, 4.0]
    const invertedRates = [5.2, 5.0, 4.4, 4.0, 3.7]
    const pts = rates => rates.map((r,i) => `${mats[i].x},${toY(r)}`).join(' ')
    return (
        <div style={{ background:'white', borderRadius:12, padding:'1rem', marginBottom:'1rem', border:'1px solid #e8ecf0' }}>
            <div style={{ textAlign:'center', fontWeight:800, fontSize:'0.75rem', color:'var(--navy)', marginBottom:'0.25rem', letterSpacing:'0.04em' }}>THE YIELD CURVE — THREE SHAPES</div>
            <svg viewBox="0 0 480 185" style={{ width:'100%', display:'block' }}>
                {/* Grid + Y labels */}
                {[2,3,4,5,6].map(r => (
                    <g key={r}>
                        <line x1="62" y1={toY(r)} x2="448" y2={toY(r)} stroke="#f0f0f0" strokeWidth="1" />
                        <text x="57" y={toY(r)+4} textAnchor="end" fill="#aaa" fontSize="9">{r}%</text>
                    </g>
                ))}
                {/* Axes */}
                <line x1="62" y1="18" x2="62" y2="153" stroke="#333" strokeWidth="1.5" />
                <line x1="62" y1="153" x2="448" y2="153" stroke="#333" strokeWidth="1.5" />
                {/* X labels */}
                {mats.map(m => (
                    <text key={m.label} x={m.x} y="165" textAnchor="middle" fill="#666" fontSize="9">{m.label}</text>
                ))}
                <text x="255" y="178" textAnchor="middle" fill="#888" fontSize="9">Time to Maturity</text>
                <text x="14" y="90" textAnchor="middle" fill="#888" fontSize="9" transform="rotate(-90,14,90)">Yield (%)</text>
                {/* Normal curve */}
                <polyline points={pts(normalRates)} fill="none" stroke="#4361ee" strokeWidth="2.5" strokeLinejoin="round" />
                {normalRates.map((r,i) => <circle key={i} cx={mats[i].x} cy={toY(r)} r="4" fill="#4361ee" />)}
                {/* Flat curve */}
                <polyline points={pts(flatRates)} fill="none" stroke="#6c757d" strokeWidth="2" strokeLinejoin="round" strokeDasharray="6,3" />
                {/* Inverted curve */}
                <polyline points={pts(invertedRates)} fill="none" stroke="#dc3545" strokeWidth="2.5" strokeLinejoin="round" />
                {invertedRates.map((r,i) => <circle key={i} cx={mats[i].x} cy={toY(r)} r="4" fill="#dc3545" />)}
                {/* Legend */}
                <line x1="68" y1="20" x2="92" y2="20" stroke="#4361ee" strokeWidth="2.5" />
                <text x="96" y="24" fill="#4361ee" fontSize="9" fontWeight="700">Normal — Healthy growth expected</text>
                <line x1="68" y1="34" x2="92" y2="34" stroke="#6c757d" strokeWidth="2" strokeDasharray="5,2" />
                <text x="96" y="38" fill="#6c757d" fontSize="9" fontWeight="700">Flat — Uncertainty / transition</text>
                <line x1="68" y1="48" x2="92" y2="48" stroke="#dc3545" strokeWidth="2.5" />
                <text x="96" y="52" fill="#dc3545" fontSize="9" fontWeight="700">Inverted — Recession warning ⚠️</text>
            </svg>
        </div>
    )
}
function BondYieldExplainer() {
    return (
        <>
            <FinnSays label="FINN EXPLAINS: WHAT IS A BOND?">
                Imagine your friend asks to borrow $1,000. They say: <em>"I'll pay you $40 every year for 10 years, then give your $1,000 back."</em> That's a bond — except the borrower is the U.S. government or a company, not your friend.
                <br /><br />
                <strong>Maturity</strong> = the end date you get your principal back (could be 1 year, 10 years, or 30 years).<br />
                <strong>Yield</strong> = what you actually earn per year as a %. If you paid $1,000 and get $40/yr, your yield is 4%. If you bought it for only $800, you still get $40/yr — so your yield is now 5%. <em>Bond price ↑ = yield ↓. Bond price ↓ = yield ↑. Always opposite.</em>
            </FinnSays>
            <BondLifecycleCard />
            <FinnSays label="FINN EXPLAINS: THE YIELD CURVE">
                Now imagine thousands of bonds — some due in 1 year, some in 30. Plot them: maturity on the x-axis, yield on the y-axis. That's the <strong>yield curve</strong>.
                <br /><br />
                Normally it slopes <strong>upward</strong> — longer loans earn more because you deserve extra reward for waiting. When short-term bonds suddenly pay <em>more</em> than long-term ones, the curve <strong>inverts</strong>. An inverted yield curve has preceded nearly every U.S. recession in modern history. <em>The Series 65 tests this constantly — know all three shapes.</em>
            </FinnSays>
            <YieldCurveChartSVG />
        </>
    )
}

// ─── Interest Rate plain-English explainer ────────────────────────────────────
function InterestRateExplainer() {
    return (
        <FinnSays label="FINN EXPLAINS: INTEREST RATES">
            An interest rate is simply the <strong>price of borrowing money.</strong> Want to borrow $200,000 for a house? The interest rate is the fee the bank charges you for letting you use their money.
            <br /><br />
            The <strong>Federal Reserve</strong> sets the key overnight rate (the Federal Funds Rate) and everything else follows. Think of it like a thermostat:
            <br />
            🌡️ <strong>Fed raises rates</strong> → borrowing costs more → people buy fewer homes and cars → businesses invest less → economy cools → inflation slows.<br />
            🌡️ <strong>Fed cuts rates</strong> → borrowing is cheap → people spend more → businesses expand → economy heats up.
            <br /><br />
            <em>Rising rates = bad for bond prices but good for savers. Falling rates = good for bond prices but bad for savings accounts. Know this cold.</em>
        </FinnSays>
    )
}

// ─── Deep Dive injection map ──────────────────────────────────────────────────
function getDiveInjection(heading, topicOrder) {
    const h = heading.toLowerCase()
    if (topicOrder === 1) {
        if (h.includes('business cycle'))                          return <><FinnBusinessCycles /><BusinessCycleDiagram /><BusinessCycleWaveSVG /><FinnGDPHistory /><RealGDPHistoryChart /></>
        if (h.includes('interest rate'))                           return <InterestRateExplainer />
        if (h.includes('monetary policy'))                         return <><PolicyComparison /></>
        if (h.includes('fiscal policy'))                           return null
        if (h.includes('money supply'))                            return <><FinnMoneySupply /><MoneySupplyJarSVG /></>
        if (h.includes('trade') && h.includes('currency'))        return <><FinnTradeAndCurrency /><TradeSeesaw /><TradeAndDollarChart /></>
        if (h.includes('inflation') && !h.includes('cpi'))        return <><FinnInflation /><InflationTimelineSVG /><IndicatorCards /></>
        if (h.includes('gross domestic product'))                  return <><GDPPlainEnglish /><GDPComponents /><GDPCalc /></>
        if (h.includes('yield curve'))                             return <BondYieldExplainer />
        if (h.includes('consumer price index') || h.includes('cpi')) return <><FinnCPI /><CPIBasketSVG /></>
    }
    if (topicOrder === 2) {
        if (h.includes('three core') || h.includes('three financial')) return <><FinnBalanceSheet /><ThreeStatements /></>
        if (h.includes('balance sheet'))                           return <><FinnBalanceSheet /><BalanceSheetEquation /></>
        if (h.includes('income statement'))                        return <><FinnIncomeStatement /><IncomeStatementWaterfall /></>
        if (h.includes('cash flow'))                               return <FinnCashFlow />
        if (h.includes('key financial ratio') || h.includes('liquidity ratio')) return <><FinnRatios /><RatioCalc /></>
        if (h.includes('valuation'))                               return <><FinnValuation /><ValuationCalc /></>
    }
    if (topicOrder === 3) {
        if (h.includes('time value') || h.includes('discounted cash flow')) return <><FinnTVM /><TVMCalc /></>
        if (h.includes('beta') && !h.includes('alpha'))            return <><FinnBeta /><BetaScale /></>
        if (h.includes('sharpe') && !h.includes('capm'))           return <><FinnSharpe /><SharpeCalc /></>
        if (h.includes('capital asset') || h.includes('capm'))     return <><FinnCAPM /><SharpeAndCAPM /><CAPMCalc /></>
        if (h.includes('types of risk'))                           return <><FinnRisk /><RiskTypes /></>
    }
    return null
}

// ─── Deep Dive View ───────────────────────────────────────────────────────────
const DIVE_LEVEL = {
    2: { bg:'#fdefc3', border:'var(--gold)', text:'var(--navy)', icon:'fa-book',     fw:900, fs:'1.05rem'  },
    3: { bg:'#fdefc3', border:'var(--gold)', text:'var(--navy)', icon:'fa-bookmark', fw:700, fs:'0.93rem' },
}
function DeepDiveView({ content, topicOrder }) {
    const sections = parseIntoSections(content)
    const vocabMap = React.useMemo(() => buildVocabMap(content), [content])
    return (
        <div style={{ animation:'fadeIn 0.3s ease' }}>
            {sections.map((sec, idx) => {
                const extra = getDiveInjection(sec.heading, topicOrder)
                const ls    = DIVE_LEVEL[sec.level] || DIVE_LEVEL[3]
                if (!sec.body.trim() && !extra) return null
                return (
                    <div key={idx} id={slugify(sec.heading)} style={{ background:'var(--white)', borderRadius:'var(--radius)', boxShadow:'var(--shadow)', marginBottom:'1rem', overflow:'hidden' }}>
                        <div style={{ background:ls.bg, borderBottom:`3px solid ${ls.border}`, padding:'0.85rem 1.5rem', display:'flex', alignItems:'center', gap:10 }}>
                            <i className={`fa-solid ${ls.icon}`} style={{ color:ls.border, fontSize:'0.85rem' }}></i>
                            <span style={{ fontWeight:ls.fw, fontSize:ls.fs, color:ls.text }}>{sec.heading}</span>
                        </div>
                        <div style={{ padding:'1.5rem' }}>
                            {sec.body.trim() && (
                                <div style={{ lineHeight:1.75, fontSize:'0.95rem', marginBottom: extra ? '0.25rem' : 0 }}
                                     dangerouslySetInnerHTML={{ __html: renderMarkdownWithTooltips(sec.body, vocabMap) }} />
                            )}
                            {extra}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ─── Quick Vocab View — auto-parses **term** – definition from content ─────────
function FlashCardModal({ items, onClose }) {
    const [index, setIndex]       = React.useState(0)
    const [flipped, setFlipped]   = React.useState(false)
    const [revealed, setRevealed] = React.useState(false)
    // mode: 'name' = show term → reveal def | 'def' = show def → reveal term | 'both' = auto-toggle each card
    const [mode, setMode]         = React.useState('name')

    const colors = ['#4361ee','#f0a500','#28a745','#dc3545','#6f42c1','#0dcaf0','#fd7e14','#6c757d']
    const card  = items[index]
    const color = colors[index % colors.length]
    const pct   = Math.round(((index + 1) / items.length) * 100)

    // In 'both' mode each card alternates: even index = name-first, odd = def-first
    const cardMode = mode === 'both' ? (index % 2 === 0 ? 'name' : 'def') : mode

    // What shows on the FRONT and BACK for this card/mode
    const front      = cardMode === 'name' ? card.term : card.def
    const back       = cardMode === 'name' ? card.def  : card.term
    const frontLabel = cardMode === 'name' ? 'Term'       : 'Definition'
    const backLabel  = cardMode === 'name' ? 'Definition' : 'Term'

    function next() { setIndex(i => (i + 1) % items.length); setFlipped(false); setRevealed(false) }
    function prev() { setIndex(i => (i - 1 + items.length) % items.length); setFlipped(false); setRevealed(false) }
    function reveal() { setFlipped(true); setRevealed(true) }
    function changeMode(m) { setMode(m); setFlipped(false); setRevealed(false) }

    const modeButtons = [
        { key:'name', icon:'fa-tag',        label:'Name'  },
        { key:'def',  icon:'fa-align-left',  label:'Def'   },
        { key:'both', icon:'fa-shuffle',     label:'Both'  },
    ]

    const modalContent = (
        <div style={{
            position:'fixed', inset:0,
            background:'linear-gradient(135deg, #0d1b2a 0%, #1a2e45 50%, #0d1b2a 100%)',
            display:'flex', flexDirection:'column',
            alignItems:'center', justifyContent:'center',
            zIndex:99999, animation:'fadeIn 0.25s ease',
        }}>
            {/* Top bar */}
            <div style={{
                position:'absolute', top:0, left:0, right:0,
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'1.2rem 2rem',
            }}>
                {/* Left: title + counter */}
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <i className="fa-solid fa-layer-group" style={{ color:'var(--gold)', fontSize:'1.1rem' }}></i>
                    <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:800, fontSize:'1rem', letterSpacing:1 }}>
                        FLASH CARDS
                    </span>
                    <span style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.85rem' }}>
                        {index + 1} / {items.length}
                    </span>
                    {mode === 'both' && (
                        <span style={{ fontSize:'0.7rem', background:'rgba(240,165,0,0.15)', color:'var(--gold)', borderRadius:20, padding:'2px 8px', fontWeight:700, letterSpacing:0.5 }}>
                            AUTO
                        </span>
                    )}
                </div>

                {/* Right: mode selector + exit */}
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {/* Mode buttons */}
                    <div style={{ display:'flex', background:'rgba(255,255,255,0.06)', borderRadius:8, border:'1px solid rgba(255,255,255,0.12)', overflow:'hidden' }}>
                        {modeButtons.map(({ key, icon, label }) => (
                            <button key={key} onClick={() => changeMode(key)} style={{
                                background: mode === key ? 'rgba(240,165,0,0.2)' : 'none',
                                border:'none',
                                borderRight: key !== 'both' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                padding:'5px 12px',
                                color: mode === key ? 'var(--gold)' : 'rgba(255,255,255,0.45)',
                                fontWeight: mode === key ? 800 : 500,
                                fontSize:'0.78rem', cursor:'pointer',
                                display:'flex', alignItems:'center', gap:5,
                                transition:'all 0.15s', letterSpacing:0.3,
                            }}>
                                <i className={`fa-solid ${icon}`} style={{ fontSize:'0.7rem' }}></i>
                                {label}
                            </button>
                        ))}
                    </div>
                    {/* Exit */}
                    <button onClick={onClose} style={{
                        background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)',
                        borderRadius:8, padding:'6px 14px', color:'rgba(255,255,255,0.7)',
                        fontWeight:700, cursor:'pointer', fontSize:'0.85rem', letterSpacing:0.5,
                        transition:'all 0.2s',
                    }}>
                        <i className="fa-solid fa-xmark me-1"></i>Exit
                    </button>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{
                position:'absolute', top:64, left:0, right:0, height:3,
                background:'rgba(255,255,255,0.08)',
            }}>
                <div style={{
                    height:'100%', width:`${pct}%`,
                    background:`linear-gradient(90deg, ${color}, var(--gold))`,
                    transition:'width 0.4s ease', borderRadius:2,
                }}/>
            </div>

            {/* Card */}
            <div style={{
                width:'min(680px, 88vw)',
                minHeight:360,
                borderRadius:20,
                padding:'3rem 3.5rem',
                background: flipped
                    ? 'rgba(255,255,255,0.96)'
                    : `linear-gradient(145deg, #162032, #1e3050)`,
                boxShadow: flipped
                    ? `0 0 0 1px ${color}55, 0 20px 60px rgba(0,0,0,0.5)`
                    : `0 0 0 1px ${color}44, 0 20px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)`,
                borderTop:`4px solid ${color}`,
                display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center',
                textAlign:'center',
                transition:'background 0.45s ease, box-shadow 0.45s ease',
                position:'relative', overflow:'hidden',
            }}>
                {/* Decorative glow blob */}
                {!flipped && <div style={{
                    position:'absolute', width:300, height:300,
                    borderRadius:'50%', background:`${color}18`,
                    filter:'blur(60px)', top:-60, right:-60, pointerEvents:'none',
                }}/>}

                <div style={{
                    fontSize:'0.7rem', fontWeight:800, letterSpacing:2, marginBottom:18,
                    color: flipped ? color : 'rgba(255,255,255,0.35)',
                    textTransform:'uppercase',
                }}>
                    {flipped ? backLabel : frontLabel}
                </div>

                <div style={{
                    fontSize: flipped ? '1.15rem' : '1.9rem',
                    fontWeight: flipped ? 500 : 800,
                    color: flipped ? '#1a1a2e' : 'var(--gold)',
                    lineHeight:1.5,
                    transition:'all 0.4s ease',
                    maxWidth:520,
                }}>
                    {flipped ? back : front}
                </div>

                {revealed && (
                    <div style={{
                        marginTop:24, paddingTop:20,
                        borderTop:`1px solid ${color}33`,
                        width:'100%',
                        fontSize:'0.82rem', color:'#888', fontStyle:'italic',
                    }}>
                        {front}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div style={{ display:'flex', alignItems:'center', gap:14, marginTop:'2.5rem' }}>
                <button onClick={prev} style={{
                    background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)',
                    borderRadius:50, width:46, height:46,
                    color:'rgba(255,255,255,0.7)', fontWeight:700,
                    cursor:'pointer', fontSize:'1rem', transition:'all 0.2s',
                    display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                    <i className="fa-solid fa-chevron-left"></i>
                </button>

                {!flipped
                    ? <button onClick={reveal} style={{
                        background:`linear-gradient(135deg, ${color}, var(--gold))`,
                        color:'#fff', border:'none',
                        borderRadius:50, padding:'12px 40px',
                        fontWeight:800, cursor:'pointer', fontSize:'0.95rem',
                        letterSpacing:0.5, boxShadow:`0 4px 20px ${color}66`,
                        transition:'all 0.2s',
                    }}>
                        <i className="fa-solid fa-eye me-2"></i>Reveal
                    </button>
                    : <button onClick={next} style={{
                        background:`linear-gradient(135deg, var(--gold), #f5c842)`,
                        color:'var(--navy)', border:'none',
                        borderRadius:50, padding:'12px 40px',
                        fontWeight:800, cursor:'pointer', fontSize:'0.95rem',
                        letterSpacing:0.5, boxShadow:'0 4px 20px rgba(240,165,0,0.45)',
                        transition:'all 0.2s',
                    }}>
                        Next <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                }

                <button onClick={next} style={{
                    background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)',
                    borderRadius:50, width:46, height:46,
                    color:'rgba(255,255,255,0.7)', fontWeight:700,
                    cursor:'pointer', fontSize:'1rem', transition:'all 0.2s',
                    display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                    <i className="fa-solid fa-chevron-right"></i>
                </button>
            </div>

            {/* Dot indicators */}
            <div style={{ display:'flex', gap:6, marginTop:'1.5rem' }}>
                {items.map((_, i) => (
                    <div key={i} onClick={() => { setIndex(i); setFlipped(false); setRevealed(false) }}
                        style={{
                            width: i === index ? 22 : 8, height:8,
                            borderRadius:4,
                            background: i === index ? color : 'rgba(255,255,255,0.2)',
                            transition:'all 0.3s ease', cursor:'pointer',
                        }}
                    />
                ))}
            </div>

            {/* Skip hint */}
            <div style={{ marginTop:'1.2rem', color:'rgba(255,255,255,0.25)', fontSize:'0.75rem' }}>
                {mode === 'both'
                    ? 'Auto mode — even cards show term first, odd cards show definition first'
                    : 'Click a dot to jump to any card'}
            </div>
        </div>
    )
    return ReactDOM.createPortal(modalContent, document.body)
}

function QuickVocabView({ content, items: propItems, label }) {
    const [showCards, setShowCards] = React.useState(false)
    // If items passed directly (ALL mode), use them; otherwise parse from content
    const items = propItems || (() => {
        const parsed = []
        if (content) {
            const re = /\*\*([^*]+)\*\*\s*[–—:-]\s*([^\n]+)/g
            let m
            while ((m = re.exec(content)) !== null) {
                parsed.push({ term: m[1].trim(), def: m[2].trim() })
            }
        }
        return parsed
    })()
    if (!items.length) return (
        <div style={{ ...infoCard, textAlign:'center', color:'var(--muted)', fontSize:'0.9rem', padding:'2rem' }}>
            <i className="fa-solid fa-circle-info fa-2x mb-3" style={{ display:'block', marginBottom:12 }}></i>
            No vocab terms found for this topic.
        </div>
    )
    const colors = ['#4361ee','#f0a500','#28a745','#dc3545','#6f42c1','#0dcaf0','#fd7e14','#6c757d']
    return (
        <div style={{ marginTop:'1.5rem', animation:'fadeIn 0.3s ease' }}>
            {showCards && <FlashCardModal items={items} onClose={() => setShowCards(false)} />}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'1rem', flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, color:'var(--muted)', fontSize:'0.82rem', fontWeight:600, letterSpacing:1 }}>
                    <i className="fa-solid fa-book-open" style={{ color:'var(--gold)' }}></i>
                    {items.length} {label ? `${label} — KEY TERMS` : 'KEY TERMS'}
                </div>
                <button onClick={() => setShowCards(true)} style={{
                    background:'var(--navy)', color:'var(--gold)',
                    border:'none', borderRadius:20,
                    padding:'4px 14px', fontSize:'0.78rem',
                    fontWeight:700, cursor:'pointer',
                    display:'flex', alignItems:'center', gap:6,
                }}>
                    <i className="fa-solid fa-layer-group"></i> Flash Cards
                </button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:'0.85rem' }}>
                {items.map((item, i) => {
                    const color = colors[i % colors.length]
                    return (
                        <div key={i} style={{ background:'var(--white)', borderRadius:10, boxShadow:'var(--shadow)', borderTop:`4px solid ${color}`, padding:'1rem 1.1rem' }}>
                            <div style={{ fontWeight:800, color, fontSize:'0.9rem', marginBottom:6, lineHeight:1.3 }}>{item.term}</div>
                            <div style={{ fontSize:'0.82rem', color:'#444', lineHeight:1.55 }}>{item.def}</div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ─── Topic → Infographic dispatcher ──────────────────────────────────────────
function TopicInfographics({ sectionOrder, topicOrder }) {
    if (sectionOrder !== 1) return null
    if (topicOrder === 1) return <><BusinessCycleDiagram /><PolicyComparison /><GDPComponents /></>
    if (topicOrder === 2) return <><BalanceSheetEquation /><IncomeStatementWaterfall /><ThreeStatements /></>
    if (topicOrder === 3) return <><BetaScale /><RiskTypes /><SharpeAndCAPM /></>
    return null
}

// ─── Topic Sidebar Item ──────────────────────────────────────────────────────
function TopicTab({ topic, active, onClick }) {
    return (
        <button
            onClick={() => onClick(topic)}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left',
                background: active ? 'var(--navy)' : 'transparent',
                color: active ? 'var(--gold)' : 'var(--text)',
                border: 'none',
                borderLeft: active ? '4px solid var(--gold)' : '4px solid transparent',
                padding: '0.7rem 1rem',
                borderRadius: '0 8px 8px 0',
                fontWeight: active ? 700 : 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: 4,
            }}
        >
            <i className={`fa-solid fa-${active ? 'book-open' : 'circle-dot'} fa-sm`}></i>
            {topic.title}
        </button>
    )
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
function SectionDetailScreen() {
    const dispatch  = useDispatch()
    const navigate  = useNavigate()
    const { id }    = useParams()

    const userLogin     = useSelector(state => state.userLogin)
    const { userInfo }  = userLogin
    const { loading, error, section } = useSelector(state => state.sectionDetail)

    const [activeTopic, setActiveTopic]       = useState(null)
    const [completing, setCompleting]         = useState(false)
    const [completeError, setCompleteError]   = useState(null)
    const [completed, setCompleted]           = useState(false)
    const [viewMode, setViewMode]             = useState('study')
    const [stickyNav, setStickyNav]           = useState(false)
    const [sidebarOpen, setSidebarOpen]       = useState(false)
    const [activeHeading, setActiveHeading]   = useState(null)
    const [showAllVocab, setShowAllVocab]     = useState(false)
    const heroRef = React.useRef(null)

    useEffect(() => {
        if (!userInfo) { navigate('/login'); return }
        dispatch(getSectionDetail(id))
    }, [dispatch, navigate, userInfo, id])

    useEffect(() => {
        if (section?.topics?.length) {
            setActiveTopic(section.topics[0])
            setCompleted(section.user_progress?.is_completed || false)
        }
    }, [section])

    // ── Scrollspy: track which heading is in view ──
    useEffect(() => {
        setActiveHeading(null)
        const stickyOffset = 96 + 24  // navbar + breadcrumb + buffer
        const getSlugs = () => {
            const slugs = []
            if (activeTopic?.content) {
                const re = /^(#{2,3}) (.+)$/gm
                let m
                while ((m = re.exec(activeTopic.content)) !== null)
                    slugs.push(slugify(m[2].trim()))
            }
            return slugs
        }
        const onScroll = () => {
            const slugs = getSlugs()
            let current = null
            for (const slug of slugs) {
                const el = document.getElementById(slug)
                if (el && el.getBoundingClientRect().top <= stickyOffset)
                    current = slug
            }
            setActiveHeading(current)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [activeTopic, viewMode])

    useEffect(() => {
        setStickyNav(false)
        const el = heroRef.current
        if (!el) return
        const navH = document.querySelector('.site-navbar')?.offsetHeight || 56
        const observer = new IntersectionObserver(
            ([entry]) => setStickyNav(!entry.isIntersecting),
            { threshold: 0, rootMargin: `-${navH}px 0px 0px 0px` }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [section?._id])

    const handleComplete = async () => {
        setCompleting(true)
        setCompleteError(null)
        try {
            const config = { headers: { Authorization: `Bearer ${userInfo.token}` } }
            await axios.post(`/api/sections/${id}/complete/`, {}, config)
            setCompleted(true)
        } catch (e) {
            setCompleteError(e.response?.data?.detail || 'Could not mark complete.')
        }
        setCompleting(false)
    }

    if (loading) return <Loader />
    if (error)   return (
        <Container>
            <Message variant="danger">{error}</Message>
            <Link to="/course" className="btn-navy" style={{ display:'inline-block', marginTop:'1rem', textDecoration:'none', padding:'0.5rem 1.25rem', borderRadius:8 }}>
                <i className="fa-solid fa-arrow-left me-2"></i>Back to Course
            </Link>
        </Container>
    )
    if (!section) return null

    const topics   = section.topics || []
    const progress = section.user_progress
    const topicIdx = topics.findIndex(t => t._id === activeTopic?._id)

    return (
        <Container fluid style={{ padding: 0 }}>

            {/* ── Sticky breadcrumb bar (appears after hero scrolls out) ── */}
            {ReactDOM.createPortal(
            <div className="snav-breadcrumb" style={{
                position:'fixed', top:56, left:0, right:0, zIndex:1050,
                background:'var(--navy)',
                borderBottom:'2px solid var(--gold)',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'0.4rem 1.25rem', gap:8,
                transform: stickyNav ? 'translateY(0)' : 'translateY(-110%)',
                transition:'transform 0.3s ease',
                boxShadow:'0 4px 20px rgba(0,0,0,0.35)',
                overflow:'hidden',
            }}>
                {/* Left: breadcrumb info */}
                <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, overflow:'hidden', flex:1 }}>
                    <Link to="/course" style={{ color:'rgba(255,255,255,0.55)', textDecoration:'none', fontSize:'0.8rem', flexShrink:0 }}>
                        <i className="fa-solid fa-graduation-cap me-1"></i>
                        <span className="snav-course-label">Course</span>
                    </Link>
                    <i className="fa-solid fa-chevron-right" style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.65rem', flexShrink:0 }}></i>
                    <span style={{ color:'var(--gold)', fontWeight:800, fontSize:'0.9rem', whiteSpace:'nowrap', flexShrink:1, overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>
                        {section.title}
                    </span>
                    <span style={{ background:'var(--gold)', color:'var(--navy)', fontWeight:700, borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', flexShrink:0, whiteSpace:'nowrap' }}>
                        {section.exam_weight}
                    </span>
                    {activeTopic && (
                        <>
                            <i className="fa-solid fa-chevron-right" style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.65rem', flexShrink:0 }}></i>
                            <span style={{ color:'rgba(255,255,255,0.75)', fontSize:'0.82rem', whiteSpace:'nowrap', flexShrink:1, overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{activeTopic.title}</span>
                        </>
                    )}
                </div>
                {/* Right: study type buttons — desktop only (mobile gets them in the navbar) */}
                <div className="snav-breadcrumb-btns" style={{ alignItems:'center', gap:6, flexShrink:0 }}>
                    {[
                        { key:'study', label:'Quick Study', icon:'book-open'             },
                        { key:'vocab', label:'Vocabulary',  icon:'bolt'                  },
                        { key:'dive',  label:'Deep Dive',   icon:'magnifying-glass-chart' },
                    ].map(btn => (
                        <button key={btn.key} onClick={() => { setViewMode(btn.key); if (btn.key !== 'vocab') setShowAllVocab(false) }} style={{
                            background: viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                            color: viewMode===btn.key ? 'var(--navy)' : 'rgba(255,255,255,0.85)',
                            border: `2px solid ${viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.25)'}`,
                            borderRadius:20, padding:'3px 12px',
                            fontSize:'0.75rem', fontWeight:700,
                            cursor:'pointer', transition:'all 0.2s',
                            display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
                        }}>
                            <i className={`fa-solid fa-${btn.icon}`}></i>{btn.label}
                        </button>
                    ))}
                </div>
            </div>
            , document.body)}

            {/* ── Study type buttons injected into the top navbar when scrolled (mobile only) ── */}
            {stickyNav && document.getElementById('navbar-study-slot') && ReactDOM.createPortal(
            <div className="snav-navbar-btns" style={{ alignItems:'center', gap:6 }}>
                <span className="snav-study-type-label" style={{ fontSize:'0.65rem', fontWeight:600, color:'rgba(255,255,255,0.45)', letterSpacing:'0.06em', whiteSpace:'nowrap', textTransform:'uppercase' }}>Study Type</span>
                {[
                    { key:'study', label:'Quick Study', icon:'book-open'             },
                    { key:'vocab', label:'Vocabulary',  icon:'bolt'                  },
                    { key:'dive',  label:'Deep Dive',   icon:'magnifying-glass-chart' },
                ].map(btn => (
                    <button key={btn.key} onClick={() => setViewMode(btn.key)} style={{
                        background: viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                        color: viewMode===btn.key ? 'var(--navy)' : 'rgba(255,255,255,0.85)',
                        border: `2px solid ${viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.25)'}`,
                        borderRadius:20, padding:'3px 10px',
                        fontSize:'0.72rem', fontWeight:700,
                        cursor:'pointer', transition:'all 0.2s',
                        display:'flex', alignItems:'center', gap:4, whiteSpace:'nowrap',
                    }}>
                        <i className={`fa-solid fa-${btn.icon}`}></i>
                        <span className="snav-btn-label">{btn.label}</span>
                    </button>
                ))}
            </div>
            , document.getElementById('navbar-study-slot'))}

            {/* ── Breadcrumb header ── */}
            <div ref={heroRef} className="hero-banner" style={{ borderRadius: '12px 12px 0 0', padding: '1.5rem 4%', marginBottom: 0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <Link to="/course" style={{ color:'rgba(255,255,255,0.65)', textDecoration:'none', fontSize:'0.85rem' }}>
                        <i className="fa-solid fa-graduation-cap me-1"></i>Course
                    </Link>
                    <i className="fa-solid fa-chevron-right" style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.7rem' }}></i>
                    <span style={{ color:'var(--gold)', fontSize:'0.85rem', fontWeight:600 }}>
                        Section {section.order}
                    </span>
                </div>
                {/* ── Desktop title (≥992px): full h1, badge lives in study row below ── */}
                <h1 className="hero-title-dt" style={{ marginTop:'0.5rem', fontSize:'1.7rem' }}>{section.title}</h1>

                {/* ── Mobile title (≤991px): truncated h1 + compact badge on same line ── */}
                <div className="hero-title-mb" style={{ alignItems:'center', gap:10, marginTop:'0.5rem', minWidth:0 }}>
                    <h1 style={{ margin:0, fontSize:'1.4rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0, flex:1 }}>{section.title}</h1>
                    <span style={{ background:'var(--gold)', color:'var(--navy)', fontWeight:700, borderRadius:20, padding:'3px 10px', fontSize:'0.75rem', flexShrink:0, whiteSpace:'nowrap' }}>
                        <i className="fa-solid fa-scale-balanced me-1"></i>{section.exam_weight}
                    </span>
                </div>

                <div className="hero-study-row" style={{ marginTop:'0.75rem' }}>
                    {/* Exam weight only shows on desktop (badge is inline with title on mobile) */}
                    <span className="hero-ew-dt" style={{ background:'var(--gold)', color:'var(--navy)', fontWeight:700, borderRadius:20, padding:'3px 12px', fontSize:'0.78rem', flexShrink:0, whiteSpace:'nowrap' }}>
                        Exam Weight: {section.exam_weight}
                    </span>
                    {/* Desktop: label + buttons inline */}
                    <div className="hero-study-inline" style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                        <span className="hero-study-label" style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.78rem', fontWeight:600 }}>Choose your study type</span>
                        <div style={{ display:'flex', gap:6 }}>
                            {[
                                { key:'study', label:'Quick Study', icon:'book-open'              },
                                { key:'vocab', label:'Vocabulary',  icon:'bolt'                   },
                                { key:'dive',  label:'Deep Dive',   icon:'magnifying-glass-chart'  },
                            ].map(btn => (
                                <button key={btn.key} onClick={() => setViewMode(btn.key)} style={{
                                    background: viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                                    color: viewMode===btn.key ? 'var(--navy)' : 'rgba(255,255,255,0.85)',
                                    border: `2px solid ${viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.3)'}`,
                                    borderRadius:20, padding:'4px 14px',
                                    fontSize:'0.8rem', fontWeight:700,
                                    cursor:'pointer', transition:'all 0.2s',
                                    display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap',
                                }}>
                                    <i className={`fa-solid fa-${btn.icon}`}></i>{btn.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mobile only: label + buttons stacked centered */}
                <div className="hero-study-mobile">
                    <div style={{ textAlign:'center', color:'rgba(255,255,255,0.55)', fontSize:'0.75rem', fontWeight:600, marginBottom:8, letterSpacing:'0.5px' }}>
                        Choose your study type
                    </div>
                    <div style={{ display:'flex', justifyContent:'center', gap:8 }}>
                        {[
                            { key:'study', label:'Quick Study', icon:'book-open'              },
                            { key:'vocab', label:'Vocabulary',  icon:'bolt'                   },
                            { key:'dive',  label:'Deep Dive',   icon:'magnifying-glass-chart'  },
                        ].map(btn => (
                            <button key={btn.key} onClick={() => setViewMode(btn.key)} style={{
                                background: viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                                color: viewMode===btn.key ? 'var(--navy)' : 'rgba(255,255,255,0.85)',
                                border: `2px solid ${viewMode===btn.key ? 'var(--gold)' : 'rgba(255,255,255,0.3)'}`,
                                borderRadius:20, padding:'4px 12px',
                                fontSize:'0.75rem', fontWeight:700,
                                cursor:'pointer', transition:'all 0.2s',
                                display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
                            }}>
                                <i className={`fa-solid fa-${btn.icon}`}></i>{btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Topic navigation pills (centered below hero) ── */}
            {activeTopic && (
                <div style={{ display:'flex', justifyContent:'center', gap:8, flexWrap:'wrap', padding:'0.85rem 1.5rem', background:'var(--white)', borderBottom:'1px solid #e9ecef' }}>
                    {topics.map((t, i) => (
                        <button
                            key={t._id}
                            onClick={() => { setActiveTopic(t); setShowAllVocab(false) }}
                            style={{
                                background: !showAllVocab && activeTopic._id === t._id ? 'var(--navy)' : '#e9ecef',
                                color: !showAllVocab && activeTopic._id === t._id ? 'var(--gold)' : 'var(--muted)',
                                border:'none', borderRadius:20,
                                padding:'5px 16px', fontSize:'0.82rem',
                                fontWeight:600, cursor:'pointer',
                                transition:'all 0.2s',
                            }}
                        >
                            {i + 1}. {t.title}
                        </button>
                    ))}
                    {/* ALL vocab button — only visible in vocab mode */}
                    {viewMode === 'vocab' && (
                        <button
                            onClick={() => setShowAllVocab(v => !v)}
                            style={{
                                background: showAllVocab ? 'var(--gold)' : '#e9ecef',
                                color: showAllVocab ? 'var(--navy)' : 'var(--muted)',
                                border: showAllVocab ? '2px solid var(--gold)' : '2px solid transparent',
                                borderRadius:20,
                                padding:'5px 16px', fontSize:'0.82rem',
                                fontWeight:800, cursor:'pointer',
                                transition:'all 0.2s',
                                letterSpacing:0.5,
                            }}
                        >
                            <i className="fa-solid fa-layer-group" style={{ marginRight:5, fontSize:'0.7rem' }}></i>ALL
                        </button>
                    )}
                </div>
            )}

            <Row style={{ margin: 0, minHeight: '80vh' }}>

                {/* ── Mobile: vertical Topics tab + slide-over drawer ── */}
                {ReactDOM.createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        className="topics-backdrop"
                        onClick={() => setSidebarOpen(false)}
                        style={{
                            position:'fixed', inset:0, zIndex:1040,
                            background:'rgba(0,0,0,0.45)',
                            opacity: sidebarOpen ? 1 : 0,
                            pointerEvents: sidebarOpen ? 'auto' : 'none',
                            transition:'opacity 0.3s ease',
                        }}
                    />
                    {/* Drawer */}
                    <div
                        className="topics-drawer"
                        style={{
                            position:'fixed', top:0, left:0, bottom:0, zIndex:1041,
                            width:'80vw',
                            background:'var(--white)',
                            boxShadow:'4px 0 24px rgba(0,0,0,0.3)',
                            transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                            transition:'transform 0.3s ease',
                            overflowY:'auto',
                            paddingTop: '4rem',
                            paddingBottom: '2rem',
                        }}
                    >
                        {/* Drawer header */}
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 1rem 1rem', borderBottom:'2px solid var(--gold)', marginBottom:8 }}>
                            <span style={{ fontWeight:800, fontSize:'1rem', color:'var(--navy)', letterSpacing:1 }}>TOPICS</span>
                            <button onClick={() => setSidebarOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)', fontSize:'1.1rem' }}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div style={{ padding:'0 0.75rem' }}>
                            {topics.map(topic => (
                                <div key={topic._id}>
                                    <TopicTab
                                        topic={topic}
                                        active={activeTopic?._id === topic._id}
                                        onClick={(t) => { setActiveTopic(t); setSidebarOpen(false) }}
                                    />
                                    {activeTopic?._id === topic._id && (() => {
                                        const headings = []
                                        if (topic.content) {
                                            const re = /^(#{2,3}) (.+)$/gm
                                            let m
                                            while ((m = re.exec(topic.content)) !== null) {
                                                headings.push({ level: m[1].length, text: m[2].trim() })
                                            }
                                        }
                                        return headings.length ? (
                                            <div style={{ marginLeft:8, marginBottom:4 }}>
                                                {headings.map((h, i) => (
                                                    <button key={i} onClick={() => {
                                                        const el = document.getElementById(slugify(h.text))
                                                        if (el) {
                                                            const stickyOffset = (document.querySelector('.site-navbar')?.offsetHeight || 56)
                                                                + (document.querySelector('.snav-breadcrumb')?.offsetHeight || 40)
                                                                + 16
                                                            const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset
                                                            window.scrollTo({ top, behavior:'smooth' })
                                                        }
                                                        setSidebarOpen(false)
                                                    }} style={{
                                                        display:'flex', alignItems:'center', gap:6,
                                                        width:'100%', textAlign:'left',
                                                        background:'none', border:'none',
                                                        padding: h.level === 2 ? '4px 8px 4px 12px' : '3px 8px 3px 22px',
                                                        fontSize: h.level === 2 ? '0.8rem' : '0.75rem',
                                                        fontWeight: h.level === 2 ? 700 : 500,
                                                        color: h.level === 2 ? 'var(--navy)' : 'var(--muted)',
                                                        cursor:'pointer',
                                                        borderLeft: h.level === 2 ? '2px solid var(--gold)' : '2px solid #e9ecef',
                                                        transition:'color 0.15s', lineHeight:1.3,
                                                    }}>
                                                        <i className={`fa-solid ${h.level === 2 ? 'fa-minus' : 'fa-circle'}`}
                                                           style={{ fontSize: h.level === 2 ? '0.55rem' : '0.35rem', color:'var(--gold)', flexShrink:0 }}></i>
                                                        {h.text}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null
                                    })()}
                                </div>
                            ))}
                            <hr />
                            <button onClick={() => window.open(`/jeopardy.html?section=${id}`, '_blank')} style={{
                                display:'flex', alignItems:'center', gap:10,
                                width:'100%', textAlign:'left',
                                background:'linear-gradient(135deg,#1a0050,#3d0099)',
                                color:'#ffe600',
                                border:'2px solid #ffe600', borderRadius:8,
                                padding:'0.7rem 1rem', fontWeight:700,
                                fontSize:'0.9rem', cursor:'pointer',
                                marginTop:8, transition:'opacity 0.2s',
                                letterSpacing:0.5,
                            }}>
                                <i className="fa-solid fa-star" style={{ fontSize:'1rem' }}></i>Play Jeopardy!
                            </button>
                            {section.has_quiz && (
                                <button onClick={() => navigate(`/quiz/${id}`)} style={{
                                    display:'flex', alignItems:'center', gap:10,
                                    width:'100%', textAlign:'left',
                                    background:'var(--navy)', color:'var(--gold)',
                                    border:'none', borderRadius:8,
                                    padding:'0.7rem 1rem', fontWeight:700,
                                    fontSize:'0.9rem', cursor:'pointer',
                                    marginTop:8, transition:'opacity 0.2s',
                                }}>
                                    <i className="fa-solid fa-pen-to-square"></i>Take Section Quiz
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Vertical tab button */}
                    <button
                        className="topics-tab-btn"
                        onClick={() => setSidebarOpen(o => !o)}
                        style={{
                            position:'fixed',
                            left: sidebarOpen ? 'calc(80vw - 2px)' : 0,
                            top:'50%',
                            transform:'translateY(-50%)',
                            zIndex:1042,
                            background:'var(--navy)',
                            color:'var(--gold)',
                            border:'2px solid var(--gold)',
                            borderLeft: sidebarOpen ? '2px solid var(--gold)' : 'none',
                            borderRadius: sidebarOpen ? '0 8px 8px 0' : '0 8px 8px 0',
                            padding:'0.6rem 0.35rem',
                            cursor:'pointer',
                            writingMode:'vertical-rl',
                            textOrientation:'mixed',
                            fontSize:'0.75rem', fontWeight:800, letterSpacing:2,
                            transition:'left 0.3s ease',
                            boxShadow:'2px 0 8px rgba(0,0,0,0.25)',
                        }}
                    >
                        {sidebarOpen ? '✕' : 'TOPICS'}
                    </button>
                </>
                , document.body)}

                {/* ── Sidebar: topic list (desktop only) ── */}
                <Col md={3} className="desktop-sidebar" style={{
                    background: 'var(--white)',
                    borderRight: '1px solid #e9ecef',
                    padding: '1.5rem 0.75rem',
                    position: 'sticky',
                    top: 96,          /* navbar (~56px) + breadcrumb (~40px) */
                    alignSelf: 'flex-start',
                    maxHeight: 'calc(100vh - 96px)',
                    overflowY: 'auto',
                }}>
                    <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--muted)', letterSpacing:1, marginBottom:12, paddingLeft:8 }}>
                        TOPICS
                    </div>
                    {topics.map(topic => (
                        <div key={topic._id}>
                            <TopicTab
                                topic={topic}
                                active={activeTopic?._id === topic._id}
                                onClick={setActiveTopic}
                            />
                            {/* Sub-headings for active topic */}
                            {activeTopic?._id === topic._id && (() => {
                                const headings = []
                                if (topic.content) {
                                    const re = /^(#{2,3}) (.+)$/gm
                                    let m
                                    while ((m = re.exec(topic.content)) !== null) {
                                        headings.push({ level: m[1].length, text: m[2].trim() })
                                    }
                                }
                                return headings.length ? (
                                    <div style={{ marginLeft:8, marginBottom:4 }}>
                                        {headings.map((h, i) => {
                                            const slug = slugify(h.text)
                                            const isActive = activeHeading === slug
                                            return (
                                            <button key={i} onClick={() => {
                                                const el = document.getElementById(slug)
                                                if (el) {
                                                    const stickyOffset = (document.querySelector('.site-navbar')?.offsetHeight || 56)
                                                        + (document.querySelector('.snav-breadcrumb')?.offsetHeight || 40)
                                                        + 16
                                                    const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset
                                                    window.scrollTo({ top, behavior:'smooth' })
                                                }
                                            }} style={{
                                                display:'flex', alignItems:'center', gap:6,
                                                width:'100%', textAlign:'left',
                                                background: isActive ? 'rgba(240,165,0,0.1)' : 'none',
                                                border:'none',
                                                padding: h.level === 2 ? '4px 8px 4px 12px' : '3px 8px 3px 22px',
                                                fontSize: h.level === 2 ? '0.8rem' : '0.75rem',
                                                fontWeight: isActive ? 700 : (h.level === 2 ? 700 : 500),
                                                color: isActive ? 'var(--gold)' : (h.level === 2 ? 'var(--navy)' : 'var(--muted)'),
                                                cursor:'pointer',
                                                borderLeft: isActive ? '3px solid var(--gold)' : (h.level === 2 ? '2px solid var(--gold)' : '2px solid #e9ecef'),
                                                transition:'color 0.15s, background 0.15s, border-left 0.15s',
                                                lineHeight:1.3,
                                                borderRadius: isActive ? '0 6px 6px 0' : 0,
                                            }}>
                                                <i className={`fa-solid ${h.level === 2 ? 'fa-minus' : 'fa-circle'}`}
                                                   style={{ fontSize: h.level === 2 ? '0.55rem' : '0.35rem', color: isActive ? 'var(--gold)' : 'var(--gold)', flexShrink:0 }}></i>
                                                {h.text}
                                            </button>
                                        )})}
                                    </div>
                                ) : null
                            })()}
                        </div>
                    ))}

                    <hr />

                    <button onClick={() => window.open(`/jeopardy.html?section=${id}`, '_blank')} style={{
                        display:'flex', alignItems:'center', gap:10,
                        width:'100%', textAlign:'left',
                        background:'linear-gradient(135deg,#1a0050,#3d0099)',
                        color:'#ffe600',
                        border:'2px solid #ffe600', borderRadius:8,
                        padding:'0.7rem 1rem', fontWeight:700,
                        fontSize:'0.9rem', cursor:'pointer',
                        marginTop:8, transition:'opacity 0.2s',
                        letterSpacing:0.5,
                    }}>
                        <i className="fa-solid fa-star" style={{ fontSize:'1rem' }}></i>Play Jeopardy!
                    </button>

                    {/* Quiz link */}
                    {section.has_quiz && (
                        <button
                            onClick={() => navigate(`/quiz/${id}`)}
                            style={{
                                display:'flex', alignItems:'center', gap:10,
                                width:'100%', textAlign:'left',
                                background: 'var(--navy)', color:'var(--gold)',
                                border:'none', borderRadius:8,
                                padding:'0.7rem 1rem', fontWeight:700,
                                fontSize:'0.9rem', cursor:'pointer',
                                marginTop:8, transition:'opacity 0.2s',
                            }}
                        >
                            <i className="fa-solid fa-pen-to-square"></i>
                            Take Section Quiz
                        </button>
                    )}
                </Col>

                {/* ── Main content area ── */}
                <Col md={9} className="content-col" style={{ padding: '1.5rem 4%' }}>

                    {activeTopic ? (
                        <>
                            {viewMode === 'vocab' && (() => {
                                if (showAllVocab) {
                                    // Gather every term from every topic, deduplicated by term name
                                    const seen = new Set()
                                    const allItems = []
                                    ;(section.topics || []).forEach(t => {
                                        if (!t.content) return
                                        const re = /\*\*([^*]+)\*\*\s*[–—:-]\s*([^\n]+)/g
                                        let m
                                        while ((m = re.exec(t.content)) !== null) {
                                            const term = m[1].trim()
                                            if (!seen.has(term)) {
                                                seen.add(term)
                                                allItems.push({ term, def: m[2].trim() })
                                            }
                                        }
                                    })
                                    return <QuickVocabView items={allItems} label="ALL TOPICS" />
                                }
                                return <QuickVocabView content={activeTopic.content} />
                            })()}
                            {viewMode === 'dive'  && <DeepDiveView content={activeTopic.content} topicOrder={activeTopic?.order} />}
                            {viewMode === 'study' && (
                                <>
                                {/* Study content */}
                                <div
                                    style={{
                                        background:'var(--white)',
                                        borderRadius:'var(--radius)',
                                        boxShadow:'var(--shadow)',
                                        padding:'2rem',
                                        lineHeight:1.75,
                                        fontSize:'0.97rem',
                                        animation:'fadeIn 0.3s ease',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderMarkdownWithTooltips(activeTopic.content, buildVocabMap(activeTopic.content)) }}
                                />
                                {/* Topic-specific infographics */}
                                <TopicInfographics sectionOrder={section.order} topicOrder={activeTopic?.order} />
                                </>
                            )}

                            {/* Prev / Next navigation */}
                            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'1.5rem' }}>
                                <button
                                    className="btn-navy"
                                    disabled={topicIdx === 0}
                                    onClick={() => setActiveTopic(topics[topicIdx - 1])}
                                    style={{ opacity: topicIdx === 0 ? 0.4 : 1, cursor: topicIdx === 0 ? 'not-allowed' : 'pointer' }}
                                >
                                    <i className="fa-solid fa-arrow-left me-2"></i>Previous
                                </button>

                                {topicIdx < topics.length - 1 ? (
                                    <button className="btn-gold" onClick={() => setActiveTopic(topics[topicIdx + 1])}>
                                        Next<i className="fa-solid fa-arrow-right ms-2"></i>
                                    </button>
                                ) : (
                                    /* Last topic — show Complete + Quiz CTA */
                                    <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                                        {!completed ? (
                                            <button
                                                className="btn-gold"
                                                onClick={handleComplete}
                                                disabled={completing}
                                            >
                                                {completing
                                                    ? <><i className="fa-solid fa-spinner fa-spin me-2"></i>Saving…</>
                                                    : <><i className="fa-solid fa-circle-check me-2"></i>Mark Complete</>
                                                }
                                            </button>
                                        ) : (
                                            <span style={{ color:'var(--success)', fontWeight:700 }}>
                                                <i className="fa-solid fa-circle-check me-2"></i>Section Complete!
                                            </span>
                                        )}
                                        {section.has_quiz && (
                                            <button className="btn-navy" onClick={() => navigate(`/quiz/${id}`)}>
                                                <i className="fa-solid fa-pen-to-square me-2"></i>Take Quiz
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            {completeError && <Message variant="danger" style={{ marginTop:'1rem' }}>{completeError}</Message>}
                        </>
                    ) : (
                        <Loader />
                    )}
                </Col>
            </Row>

            <style>{`
                @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
            `}</style>
        </Container>
    )
}

export default SectionDetailScreen
